import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Modal,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Drawer,
  Divider,
  Popconfirm,
  Tooltip,
  DatePicker,
  Menu,
  Checkbox,
  Dropdown, Form,
  Radio,
} from 'antd';

// 超售確認模式：CORRECTION = 實物足夠（系統數量有誤，自動校正並全數出貨）；
// SHORTAGE = 實物不足（差額記入超售欠貨，補貨後自動建立補發單）
type OversellMode = 'CORRECTION' | 'SHORTAGE';

// Auto-proceed with the SHORTAGE path whenever the backend reports insufficient
// stock, skipping the previous confirmation modal.
//
// Rationale: operators overwhelmingly chose SHORTAGE (it was tagged 「常用」in
// the removed modal), and it is the CONSERVATIVE choice — we ship what is
// physically on the shelf and record the shortfall as backorder, to be
// fulfilled from a follow-up 補發單 once stock arrives. The CORRECTION path
// silently bumps system inventory upward to match the operator's claim, which
// is the dangerous one; keeping that off the auto path is deliberate.
//
// A brief info toast lets the operator see that auto-shortage kicked in
// (dev tools console.warn carries the full warning + item list for auditing).
// The signature is preserved so the three existing callers still compile;
// the onCancelExtra callback is now unreachable because there is no user-
// visible cancel path any more.
const showOversellConfirmDialog = (
  t: TFunction,
  warning: string,
  insufficientItems: any[],
  onConfirm: (mode: OversellMode) => void | Promise<void>,
  _onCancelExtra?: () => void,
) => {
  const count = Array.isArray(insufficientItems) ? insufficientItems.length : 0;
  console.warn('[oversell auto-shortage] proceeding without confirmation:', { warning, insufficientItems });
  message.info(
    count > 0
      ? t('系統庫存不足（{{count}} 項 SKU），已自動使用「實物不足，先出現有數量」模式處理', { count })
      : t('系統庫存不足，已自動使用「實物不足，先出現有數量」模式處理'),
    3,
  );
  // Fire and forget — the caller uses the same onConfirm callback it always did.
  Promise.resolve(onConfirm('SHORTAGE')).catch((err) => {
    console.error('[oversell auto-shortage] onConfirm failed:', err);
  });
};
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UploadOutlined,
  FileExcelOutlined,
  EyeOutlined,
  PrinterOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  InboxOutlined,
  UserOutlined,
  GlobalOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SettingOutlined,
  ReloadOutlined,
  UpOutlined,
  DownOutlined,
  CloseCircleOutlined,
  SendOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import useDraggableColumns from '../../../components/table/useDraggableColumns';
// import type { UploadProps } from 'antd';
import dayjs from 'dayjs';
import OutboundOrderForm from '../components/OutboundOrderForm';
import apiService from '../../../services/apiService';
import HktvRecentErrorsBanner from '../../integrations/hktvmall/HktvRecentErrorsBanner';
import OutboundBulkUploadModal from '../components/BulkUploadModal';
import ReturnOrderModal from '../components/ReturnOrderModal';
import DeliveryProviderModal from '../components/DeliveryProviderModal';
import {getHktvMallWaybillHtml, getWaybillHtml, printHktvMallWaybillAuto, printWaybillAuto, printBatchWaybillsOnProcessing} from "@utils/pdfUtils.ts";
import { useTranslation } from "react-i18next";
import type { TFunction } from 'i18next';
import { useStatusLabel } from '../../../i18n/statusLabel';

const { Title, Text } = Typography;
const { Option } = Select;

// 出庫訂單狀態類型
export type OutboundOrderStatus =
    | '待發貨'
    | '處理中'
    | '配貨中'
    | '核對中'
    | '已包裝'
    | '待攬收'
    | '已發貨'
    | '已收貨'
    | '申請退貨'
    | '退貨處理中'
    | '退貨完成'
    | '已取消';

// 產品行項目接口
export interface OutboundOrderItem {
  id: string;
  productNameEN: string;
  productNameCN: string;
  skuId: string;
  productBarcode?: string;
  quantityPackage: number;
  quantityBaseUnit: number;
  brand: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  unitPrice: number;
  discountAmount?: number;
  allocateRule: string;
  remark?: string;
  availableStock?: number; // 可用庫存
  isStockInsufficient?: boolean; // 庫存不足標記
  pickedFromLocation?: string; // 取貨庫位路徑 (e.g., "ZONE/5/F/AT03-1-003")
  pickedFromLocationId?: string; // 取貨庫位 UUID
  lotNumber?: string; // 批次號
  expiryDate?: string; // 到期日
}

// 退貨信息接口
export interface ReturnInfo {
  requestDate: string;
  reason: string;
  merchantConfirmation?: string;
  handlingStatus: 'keep' | 'discard' | 'refund' | 'exchange';
  handlingRemark?: string;
  confirmedBy?: string;
  confirmedDate?: string;
  inventoryAdjusted?: boolean;
  adjustmentDate?: string;
}

// 出庫訂單接口
export interface OutboundOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  customerCode: string;
  items: OutboundOrderItem[];
  courier: string;
  receiver: string;
  countryCode: string;
  deliveryAddress: string;
  deliveryTrackingNumber?: string;
  waybillNumber?: string;
  deliveryRemark?: string;
  otherRemarks?: string;  // 其他備註 - displayed in DN/waybill
  salesChannel?: string;
  status: OutboundOrderStatus;
  remark?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  totalValue: number;
  orderDiscount?: number;
  hasStockIssues: boolean;
  returnInfo?: ReturnInfo;
  pickUpDate?: string;
  // FedEx/Delivery Provider fields
  deliveryProvider?: string;
  fedexTrackingNo?: string;
  fedexLabelPdf?: string;  // Full PDF data (from detail view only)
  hasFedExLabel?: boolean; // Boolean flag (from list view)
  // SF Express fields
  sfExpressLabelPdf?: string; // SF Express waybill PDF (base64)
  // Shipping address fields (from Shopify)
  receiverPhone?: string;
  postalCode?: string;
  city?: string;
  province?: string; // State/province code (e.g. QLD, VIC, NY) for the FedEx state dropdown
}

// 模擬產品數據
const mockProducts = [
  {
    id: 'P001',
    nameEN: 'Wireless Headphones',
    nameCN: '無線耳機',
    skuId: 'WH-001',
    barcodePackage: '1234567890123',
    barcodeBaseUnit: '1234567890124',
    brand: 'TechBrand',
    length: 20,
    width: 15,
    height: 8,
    weight: 0.5,
    unitPrice: 99.99,
    availableStock: 150,
    defaultAllocateRule: 'FIFO'
  },
  {
    id: 'P002',
    nameEN: 'Bluetooth Speaker',
    nameCN: '藍牙音箱',
    skuId: 'BS-002',
    barcodePackage: '2234567890123',
    barcodeBaseUnit: '2234567890124',
    brand: 'AudioMax',
    length: 25,
    width: 12,
    height: 10,
    weight: 1.2,
    unitPrice: 79.99,
    availableStock: 80,
    defaultAllocateRule: 'FIFO'
  },
  {
    id: 'P003',
    nameEN: 'Smart Watch',
    nameCN: '智能手錶',
    skuId: 'SW-003',
    barcodePackage: '3234567890123',
    barcodeBaseUnit: '3234567890124',
    brand: 'SmartTech',
    length: 5,
    width: 4,
    height: 1.5,
    weight: 0.08,
    unitPrice: 199.99,
    availableStock: 25,
    defaultAllocateRule: 'LIFO'
  }
];

// 模擬配送商數據
// Built-in couriers. Users add their own through the 快遞商 dropdown; those are
// stored per company (option-lists API) and merged in at runtime. Built-ins are
// never removable, so this list stays the floor everyone sees.
const DEFAULT_COURIERS = [
  '自取',
  '卓爾',
  '恆冠',
  'GOGOX',
  'GOGOX (STP A/C)',
  '順豐',
  '順豐 (STP A/C)',
  '京東',
  '京東 (STP A/C)',
  'DHL',
  'Fedex',
  'UPS'
];

// 模擬國家或地區數據
const mockCountryCodes = (t: TFunction) => [
  { value: 'CN', label: t('中國 (+86)') },
  { value: 'HK', label: t('香港 (+852)') },
  { value: 'MO', label: t('澳門 (+853)') },
  { value: 'TW', label: t('台灣 (+886)') },
  { value: 'US', label: t('美國 (+1)') },
  { value: 'UK', label: t('英國 (+44)') },
  { value: 'JP', label: t('日本 (+81)') },
  { value: 'KR', label: t('韓國 (+82)') },
  { value: 'SG', label: t('新加坡 (+65)') },
  { value: 'MY', label: t('馬來西亞 (+60)') },
  { value: 'TH', label: t('泰國 (+66)') },
  { value: 'AU', label: t('澳洲 (+61)') },
  { value: 'CA', label: t('加拿大 (+1)') },
  { value: 'FR', label: t('法國 (+33)') },
  { value: 'DE', label: t('德國 (+49)') },
];

// 分配規則選項
const allocateRules = (t: TFunction) => [
  { value: 'FIFO', label: t('FIFO (先進先出)') },
  { value: 'LIFO', label: t('LIFO (後進先出)') },
  { value: 'FEFO', label: t('FEFO (先到期先出)') },
  { value: 'MANUAL', label: t('手動分配') },
];

// 模擬出庫訂單數據（暫不使用）
/* const mockOutboundOrders: OutboundOrder[] = [
  {
    id: 'OB001',
    orderNumber: 'OUT-2024-001',
    customerName: '客戶A',
    customerCode: 'CUST001',
    items: [
      {
        id: 'item1',
        productNameEN: 'Wireless Headphones',
        productNameCN: '無線耳機',
        skuId: 'WH-001',
        barcodePackage: '1234567890123',
        barcodeBaseUnit: '1234567890124',
        quantityPackage: 10,
        quantityBaseUnit: 10,
        brand: 'TechBrand',
        length: 20,
        width: 15,
        height: 8,
        weight: 0.5,
        unitPrice: 99.99,
        allocateRule: 'FIFO',
        availableStock: 150,
        isStockInsufficient: false
      }
    ],
    courier: 'DHL',
    receiver: '李先生',
    countryCode: 'CN',
    deliveryAddress: '廣東省深圳市南山區科技園',
    deliveryTrackingNumber: 'DHL123456789',
    salesChannel: 'HKTV Mall',
    status: '已發貨',
    totalValue: 999.90,
    hasStockIssues: false,
    createdAt: '2024-01-15 10:30:00',
    updatedAt: '2024-01-15 16:20:00',
    createdBy: '張三'
  },
  {
    id: 'OB002',
    orderNumber: 'OUT-2024-002',
    customerName: '客戶B',
    customerCode: 'CUST002',
    items: [
      {
        id: 'item2',
        productNameEN: 'Smart Watch',
        productNameCN: '智能手錶',
        skuId: 'SW-003',
        barcodePackage: '3234567890123',
        barcodeBaseUnit: '3234567890124',
        quantityPackage: 50,
        quantityBaseUnit: 50,
        brand: 'SmartTech',
        length: 5,
        width: 4,
        height: 1.5,
        weight: 0.08,
        unitPrice: 199.99,
        allocateRule: 'LIFO',
        availableStock: 25,
        isStockInsufficient: true
      }
    ],
    courier: '順豐',
    receiver: '王女士',
    countryCode: 'CN',
    deliveryAddress: '上海市浦東新區陸家嘴金融中心',
    salesChannel: 'Shopify',
    status: '待發貨',
    totalValue: 9999.50,
    hasStockIssues: true,
    createdAt: '2024-01-15 14:20:00',
    updatedAt: '2024-01-15 14:20:00',
    createdBy: '李四'
  },
  {
    id: 'OB003',
    orderNumber: 'OUT-2024-003',
    customerName: '客戶C',
    customerCode: 'CUST003',
    items: [
      {
        id: 'item3',
        productNameEN: 'Bluetooth Speaker',
        productNameCN: '藍牙音箱',
        skuId: 'BS-002',
        barcodePackage: '2234567890123',
        barcodeBaseUnit: '2234567890124',
        quantityPackage: 5,
        quantityBaseUnit: 5,
        brand: 'AudioMax',
        length: 25,
        width: 12,
        height: 10,
        weight: 1.2,
        unitPrice: 79.99,
        allocateRule: 'FIFO',
        availableStock: 80,
        isStockInsufficient: false
      }
    ],
    courier: 'FedEx',
    receiver: '陳先生',
    countryCode: 'CN',
    deliveryAddress: '北京市朝陽區國貿中心',
    deliveryTrackingNumber: 'FDX987654321',
    salesChannel: 'Amazon',
    status: '已收貨',
    totalValue: 399.95,
    hasStockIssues: false,
    createdAt: '2024-01-10 09:15:00',
    updatedAt: '2024-01-14 15:30:00',
    createdBy: '王五'
  },
  {
    id: 'OB004',
    orderNumber: 'OUT-2024-004',
    customerName: '客戶D',
    customerCode: 'CUST004',
    items: [
      {
        id: 'item4',
        productNameEN: 'USB-C Cable',
        productNameCN: 'USB-C 數據線',
        skuId: 'UC-004',
        barcodePackage: '4234567890123',
        barcodeBaseUnit: '4234567890124',
        quantityPackage: 20,
        quantityBaseUnit: 20,
        brand: 'CablePro',
        length: 1,
        width: 1,
        height: 1,
        weight: 0.05,
        unitPrice: 9.99,
        allocateRule: 'FIFO',
        availableStock: 500,
        isStockInsufficient: false
      }
    ],
    courier: 'UPS',
    receiver: '趙先生',
    countryCode: 'CN',
    deliveryAddress: '杭州市西湖區文三路',
    deliveryTrackingNumber: '',
    salesChannel: 'eBay',
    status: '處理中',
    totalValue: 199.80,
    hasStockIssues: false,
    createdAt: '2024-01-16 09:00:00',
    updatedAt: '2024-01-16 09:30:00',
    createdBy: '王六'
  },
  {
    id: 'OB005',
    orderNumber: 'OUT-2024-005',
    customerName: '客戶E',
    customerCode: 'CUST005',
    items: [
      {
        id: 'item5',
        productNameEN: 'Laptop Stand',
        productNameCN: '筆電支架',
        skuId: 'LS-005',
        barcodePackage: '5234567890123',
        barcodeBaseUnit: '5234567890124',
        quantityPackage: 10,
        quantityBaseUnit: 10,
        brand: 'ErgoTech',
        length: 30,
        width: 25,
        height: 5,
        weight: 0.9,
        unitPrice: 39.99,
        allocateRule: 'FIFO',
        availableStock: 60,
        isStockInsufficient: false
      }
    ],
    courier: '中通快遞',
    receiver: '孫女士',
    countryCode: 'CN',
    deliveryAddress: '南京市秦淮區中山南路',
    deliveryTrackingNumber: '',
    salesChannel: 'Taobao',
    status: '配貨中',
    totalValue: 399.90,
    hasStockIssues: false,
    createdAt: '2024-01-16 10:00:00',
    updatedAt: '2024-01-16 10:20:00',
    createdBy: '周一'
  },
  {
    id: 'OB006',
    orderNumber: 'OUT-2024-006',
    customerName: '客戶F',
    customerCode: 'CUST006',
    items: [
      {
        id: 'item6',
        productNameEN: 'Mouse Pad',
        productNameCN: '滑鼠墊',
        skuId: 'MP-006',
        barcodePackage: '6234567890123',
        barcodeBaseUnit: '6234567890124',
        quantityPackage: 30,
        quantityBaseUnit: 30,
        brand: 'SoftPad',
        length: 25,
        width: 20,
        height: 0.5,
        weight: 0.12,
        unitPrice: 5.99,
        allocateRule: 'FEFO',
        availableStock: 200,
        isStockInsufficient: false
      }
    ],
    courier: '圓通速遞',
    receiver: '周先生',
    countryCode: 'CN',
    deliveryAddress: '重慶市渝中區解放碑',
    deliveryTrackingNumber: '',
    status: '核對中',
    totalValue: 179.70,
    hasStockIssues: false,
    createdAt: '2024-01-16 11:00:00',
    updatedAt: '2024-01-16 11:10:00',
    createdBy: '吳二'
  },
  {
    id: 'OB007',
    orderNumber: 'OUT-2024-007',
    customerName: '客戶G',
    customerCode: 'CUST007',
    items: [
      {
        id: 'item7',
        productNameEN: 'Keyboard',
        productNameCN: '鍵盤',
        skuId: 'KB-007',
        barcodePackage: '7234567890123',
        barcodeBaseUnit: '7234567890124',
        quantityPackage: 12,
        quantityBaseUnit: 12,
        brand: 'KeyMaster',
        length: 45,
        width: 15,
        height: 4,
        weight: 0.7,
        unitPrice: 49.99,
        allocateRule: 'FIFO',
        availableStock: 40,
        isStockInsufficient: false
      }
    ],
    courier: '申通快遞',
    receiver: '錢先生',
    countryCode: 'CN',
    deliveryAddress: '天津市和平區濱江道',
    deliveryTrackingNumber: '',
    status: '已包裝',
    totalValue: 599.88,
    hasStockIssues: false,
    createdAt: '2024-01-16 12:00:00',
    updatedAt: '2024-01-16 12:20:00',
    createdBy: '鄭三'
  },
  {
    id: 'OB008',
    orderNumber: 'OUT-2024-008',
    customerName: '客戶H',
    customerCode: 'CUST008',
    items: [
      {
        id: 'item8',
        productNameEN: 'Webcam',
        productNameCN: '攝像頭',
        skuId: 'WC-008',
        barcodePackage: '8234567890123',
        barcodeBaseUnit: '8234567890124',
        quantityPackage: 8,
        quantityBaseUnit: 8,
        brand: 'CamPlus',
        length: 8,
        width: 5,
        height: 4,
        weight: 0.2,
        unitPrice: 59.99,
        allocateRule: 'FIFO',
        availableStock: 100,
        isStockInsufficient: false
      }
    ],
    courier: '韻達速遞',
    receiver: '吳女士',
    countryCode: 'CN',
    deliveryAddress: '成都市武侯區人民南路',
    deliveryTrackingNumber: '',
    status: '待攬收',
    totalValue: 479.92,
    hasStockIssues: false,
    createdAt: '2024-01-16 13:00:00',
    updatedAt: '2024-01-16 13:15:00',
    createdBy: '錢七'
  },
  {
    id: 'OB009',
    orderNumber: 'OUT-2024-009',
    customerName: '客戶I',
    customerCode: 'CUST009',
    items: [
      {
        id: 'item9',
        productNameEN: 'External HDD',
        productNameCN: '移動硬碟',
        skuId: 'EH-009',
        barcodePackage: '9234567890123',
        barcodeBaseUnit: '9234567890124',
        quantityPackage: 6,
        quantityBaseUnit: 6,
        brand: 'StoreX',
        length: 12,
        width: 8,
        height: 2,
        weight: 0.25,
        unitPrice: 79.99,
        allocateRule: 'FIFO',
        availableStock: 30,
        isStockInsufficient: false
      }
    ],
    courier: 'DHL',
    receiver: '鄒先生',
    countryCode: 'CN',
    deliveryAddress: '武漢市江漢區江漢路',
    deliveryTrackingNumber: 'DHL999999999',
    status: '申請退貨',
    totalValue: 479.94,
    hasStockIssues: false,
    createdAt: '2024-01-12 10:00:00',
    updatedAt: '2024-01-16 14:00:00',
    createdBy: '佘八'
  },
  {
    id: 'OB010',
    orderNumber: 'OUT-2024-010',
    customerName: '客戶J',
    customerCode: 'CUST010',
    items: [
      {
        id: 'item10',
        productNameEN: 'HDMI Cable',
        productNameCN: 'HDMI 線',
        skuId: 'HC-010',
        barcodePackage: '1334567890123',
        barcodeBaseUnit: '1334567890124',
        quantityPackage: 15,
        quantityBaseUnit: 15,
        brand: 'CablePro',
        length: 2,
        width: 1,
        height: 1,
        weight: 0.1,
        unitPrice: 12.99,
        allocateRule: 'FIFO',
        availableStock: 100,
        isStockInsufficient: false
      }
    ],
    courier: 'FedEx',
    receiver: '雷女士',
    countryCode: 'CN',
    deliveryAddress: '合肥市包河區徽州大道',
    deliveryTrackingNumber: 'FDX111111111',
    status: '退貨處理中',
    totalValue: 194.85,
    hasStockIssues: false,
    createdAt: '2024-01-11 10:00:00',
    updatedAt: '2024-01-16 15:00:00',
    createdBy: '張九'
  },
  {
    id: 'OB011',
    orderNumber: 'OUT-2024-011',
    customerName: '客戶K',
    customerCode: 'CUST011',
    items: [
      {
        id: 'item11',
        productNameEN: 'USB Hub',
        productNameCN: 'USB 集線器',
        skuId: 'UH-011',
        barcodePackage: '2334567890123',
        barcodeBaseUnit: '2334567890124',
        quantityPackage: 9,
        quantityBaseUnit: 9,
        brand: 'Hubman',
        length: 10,
        width: 4,
        height: 2,
        weight: 0.15,
        unitPrice: 24.99,
        allocateRule: 'FIFO',
        availableStock: 70,
        isStockInsufficient: false
      }
    ],
    courier: 'TNT',
    receiver: '常先生',
    countryCode: 'CN',
    deliveryAddress: '福州市鼓樓區東街口',
    deliveryTrackingNumber: 'TNT222222222',
    status: '退貨完成',
    totalValue: 224.91,
    hasStockIssues: false,
    createdAt: '2024-01-10 10:00:00',
    updatedAt: '2024-01-16 16:00:00',
    createdBy: '李十'
  }
]; */

const OutboundOrderPage: React.FC = () => {
    const { t } = useTranslation();
    const statusLabel = useStatusLabel();
  const location = useLocation();
  const { user } = useAuthStore();
  const isCustomerRole = user?.role === 'CUSTOMER';

  // Check if CUSTOMER role is accessing without required itemSku filter
  // CUSTOMER can only access this page via filtered link from stock management
  const searchParams = new URLSearchParams(location.search);
  const hasItemSkuFilter = !!searchParams.get('itemSku');

  // If CUSTOMER role and no itemSku filter, show permission denied
  if (isCustomerRole && !hasItemSkuFilter) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <h3>{t('權限不足')}</h3>
        <p>{t('請從庫存管理頁面的「總下單數量（PENDING）」連結進入')}</p>
        <p style={{ color: '#999', fontSize: '12px' }}>{t('Access via filtered link from Stock Management only')}</p>
      </div>
    );
  }

  const [customCouriers, setCustomCouriers] = useState<string[]>([]);
  const courierOptions = React.useMemo(
    () => [...DEFAULT_COURIERS, ...customCouriers.filter(c => !DEFAULT_COURIERS.includes(c))],
    [customCouriers]
  );
  const [outboundOrders, setOutboundOrders] = useState<OutboundOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OutboundOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OutboundOrder | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState<OutboundOrder | null>(null);
  const [bulkUploadVisible, setBulkUploadVisible] = useState(false);
  const [returnModalVisible, setReturnModalVisible] = useState(false);
  const [returningOrder, setReturningOrder] = useState<OutboundOrder | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Delivery Provider Modal state
  const [deliveryModalVisible, setDeliveryModalVisible] = useState(false);
  const [deliveryModalOrder, setDeliveryModalOrder] = useState<OutboundOrder | null>(null);
  
  // Update Pick Up Date modal state
  const [updatePickUpDateModalVisible, setUpdatePickUpDateModalVisible] = useState(false);
  const [updatePickUpDateRange, setUpdatePickUpDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [updatePickUpDateLoading, setUpdatePickUpDateLoading] = useState(false);
  const [retryingLabel, setRetryingLabel] = useState(false);

  // 出庫訂單表格排序狀態
  const [outboundSortField, setOutboundSortField] = useState<string>('');
  const [outboundSortOrder, setOutboundSortOrder] = useState<'asc' | 'desc' | ''>('');
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [customers, setCustomers] = useState<Array<{ value: string; label: string; code: string }>>([]);
  const [runTags, setRunTags] = useState<Array<{ id: string; tagName: string; storeCodes: string[]; warehouseIds: string[] }>>([]);
  const [hktvAccounts, setHktvAccounts] = useState<Array<{ storeCode: string; status: string }>>([]);

  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.getWarehouses({ limit: 1000 });
        const list: any[] = res?.data || res?.warehouses || [];
        const mapped = list.map((w: any) => ({ id: w.id || w.code || w.warehouseId, name: w.name || w.warehouseName || w.code }));
        setWarehouses(mapped);
      } catch {}
    })();
  }, []);

  // Load run tags with warehouse IDs
  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.getRunTagsWithWarehouseIds();
        if (res?.success && Array.isArray(res?.data)) {
          setRunTags(res.data);
        }
      } catch (error) {
        console.error('Failed to load run tags:', error);
      }
    })();
  }, []);

  // Load customers from API
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.getCustomers({ limit: 1000, isActive: true });
        if (cancelled) return;
        const list: any[] = res?.customers || res?.data || [];
        // Deduplicate by name — if same customer name exists in multiple companies, keep the first one
        const seen = new Map<string, any>();
        for (const c of list) {
          const key = (c.name || '').trim().toLowerCase();
          if (!seen.has(key)) {
            seen.set(key, c);
          }
        }
        const mapped = Array.from(seen.values()).map((c: any) => ({
          value: c.id,
          label: c.name,
          code: c.id
        }));
        setCustomers(mapped);
      } catch (error) {
        console.error('Failed to load customers:', error);
        if (!cancelled) setCustomers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load HKTV accounts from API
  useEffect(() => {
    (async () => {
      try {
        const result: any = await apiService.get('/integrations/hktvmall/accounts');
        if (result?.success) {
          const list: any[] = result?.data || [];
          setHktvAccounts(list);
        }
      } catch (error) {
        console.error('Failed to load HKTV accounts:', error);
        setHktvAccounts([]);
      }
    })();
  }, []);


  // Couriers a user added for this company, merged over the built-ins.
  useEffect(() => {
    (async () => {
      try {
        const result: any = await apiService.get('/option-lists/courier');
        if (result?.success) setCustomCouriers(result.data || []);
      } catch (error) {
        // A failed load must not block order entry — fall back to the built-ins.
        console.error('Failed to load courier options:', error);
        setCustomCouriers([]);
      }
    })();
  }, []);

  const handleAddCourier = async (value: string) => {
    // Optimistic: the dropdown stays responsive, and a failure rolls back.
    setCustomCouriers(prev => (prev.includes(value) ? prev : [...prev, value]));
    try {
      const result: any = await apiService.post('/option-lists/courier/values', { value });
      if (result?.success) setCustomCouriers(result.data || []);
    } catch (error: any) {
      setCustomCouriers(prev => prev.filter(v => v !== value));
      message.error(t('新增快遞商失敗') + (error?.message ? `: ${error.message}` : ''));
    }
  };

  const handleRemoveCourier = async (value: string) => {
    const previous = customCouriers;
    setCustomCouriers(prev => prev.filter(v => v !== value));
    try {
      const result: any = await apiService.delete(`/option-lists/courier/values?value=${encodeURIComponent(value)}`);
      if (result?.success) setCustomCouriers(result.data || []);
    } catch (error: any) {
      setCustomCouriers(previous);
      message.error(t('移除快遞商失敗') + (error?.message ? `: ${error.message}` : ''));
    }
  };

  // HKTVmall 運單自動打印（同時生成PDF）
  const handleHktvMallDownloadPDF = async (orderNumber: string, waybillNumber: string) => {
    // 自動打印運單並同時生成PDF下載
    await printHktvMallWaybillAuto(orderNumber, waybillNumber);
  };

  // 普通運單自動打印（同時生成PDF）
  const handleDownloadPDF = async (orderNumber: string, waybillNumber: string) => {
    // 自動打印運單並同時生成PDF下載
    await printWaybillAuto(orderNumber, waybillNumber);
  };


  // 出庫訂單表格列配置狀態
  const [outboundColumnSettings, setOutboundColumnSettings] = useState(() => {
    const saved = localStorage.getItem('outbound-order-columns-config');
    const version = localStorage.getItem('outbound-order-columns-version');

    // 檢查版本，如果不是最新版本則重置配置
    if (version !== '2024-01-v5' || !saved) {
      const defaultConfig = {
        orderNumber: { visible: true, order: 0 },
        customerName: { visible: true, order: 1 },
        customerCode: { visible: true, order: 2 },
        receiver: { visible: true, order: 3 },
        deliveryAddress: { visible: true, order: 4 },
        courier: { visible: true, order: 5 },
        salesChannel: { visible: true, order: 6 },
        pickUpDate: { visible: true, order: 7 },
        trackingNumber: { visible: false, order: 8 },
        waybill: { visible: true, order: 9 },
        status: { visible: true, order: 10 },
        totalValue: { visible: true, order: 11 },
        createdAt: { visible: true, order: 12 },
        actions: { visible: true, order: 13 }
      };
      localStorage.setItem('outbound-order-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('outbound-order-columns-version', '2024-01-v5');
      return defaultConfig;
    }

    try {
      return JSON.parse(saved);
    } catch {
      const defaultConfig = {
        orderNumber: { visible: true, order: 0 },
        customerName: { visible: true, order: 1 },
        customerCode: { visible: true, order: 2 },
        receiver: { visible: true, order: 3 },
        deliveryAddress: { visible: true, order: 4 },
        courier: { visible: true, order: 5 },
        salesChannel: { visible: true, order: 6 },
        pickUpDate: { visible: true, order: 7 },
        trackingNumber: { visible: false, order: 8 },
        waybill: { visible: true, order: 9 },
        status: { visible: true, order: 10 },
        totalValue: { visible: true, order: 11 },
        createdAt: { visible: true, order: 12 },
        actions: { visible: true, order: 13 }
      };
      return defaultConfig;
    }
  });

  // 處理出庫訂單列排序
  const handleOutboundColumnSort = (columnKey: string) => {
    if (outboundSortField === columnKey) {
      setOutboundSortOrder(outboundSortOrder === 'asc' ? 'desc' : outboundSortOrder === 'desc' ? '' : 'asc');
      if (outboundSortOrder === 'desc') {
        setOutboundSortField('');
      }
    } else {
      setOutboundSortField(columnKey);
      setOutboundSortOrder('asc');
    }
  };

  // 獲取出庫訂單排序圖標
  const getOutboundSortIcon = (columnKey: string) => {
    if (outboundSortField !== columnKey) return null;
    return outboundSortOrder === 'asc' ?
        <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
        <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 篩選狀態 - 從 URL 參數初始化（支援從庫存詳情跳轉）
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    const itemSkuFromUrl = params.get('itemSku');
    const statusFromUrl = params.get('status');
    return {
      status: statusFromUrl || undefined as string | undefined,
      customer: '',
      hasStockIssues: undefined as string | undefined,
      searchText: '',
      salesChannel: undefined as string | undefined,
      pickUpDateRange: undefined as [string, string] | undefined,
      createdDateRange: undefined as [string, string] | undefined,
      storeId: [] as string[],
      runTagId: [] as string[],
      itemSku: itemSkuFromUrl || ''
    };
  });

  // 當 URL 參數變化時更新篩選條件（支援從庫存詳情跳轉）
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const itemSku = params.get('itemSku');
    const status = params.get('status');
    // 只在 URL 參數與當前 filters 不同時更新，避免不必要的重新渲染
    if ((itemSku && itemSku !== filters.itemSku) || (status && status !== filters.status)) {
      setFilters(prev => ({
        ...prev,
        itemSku: itemSku || prev.itemSku,
        status: status || prev.status
      }));
    }
  }, [location.search]);

  // 列設置下拉菜單可見狀態
  const [outboundColumnSettingsVisible, setOutboundColumnSettingsVisible] = useState(false);

  // 展開收起控制
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggleCollapsed = () => setCollapsed(prev => !prev);

  // 報告確認彈窗狀態
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportForm] = Form.useForm();
  const [reportType, setReportType] = useState<'waybill' | 'hktvmall' | 'bundle-movements'>('waybill');

  // 受控分頁（後端返回 total）
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  // 處理報告按鈕點擊
  const handleReportClick = (type: 'waybill' | 'hktvmall' | 'bundle-movements') => {
    setReportType(type);
    setReportModalVisible(true);
  };

  // 處理報告確認
  const handleReportConfirm = async () => {
    try {
      const values = await reportForm.validateFields();
      
      if (reportType === 'waybill') {
        // 現有的取貨日期運單列表報告邏輯
        const params: any = {};
        
        // 處理取貨日期範圍
        if (values.pickUpDateRange && values.pickUpDateRange.length === 2) {
          params.pickUpDateStart = values.pickUpDateRange[0].format('YYYY-MM-DD');
          params.pickUpDateEnd = values.pickUpDateRange[1].format('YYYY-MM-DD');
        }
        
        // 處理揀貨創建時間範圍
        if (values.pickOrderCreateDateRange && values.pickOrderCreateDateRange.length === 2) {
          params.pickOrderCreateDateStart = values.pickOrderCreateDateRange[0].format('YYYY-MM-DD');
          params.pickOrderCreateDateEnd = values.pickOrderCreateDateRange[1].format('YYYY-MM-DD');
        }
        
        // 處理標籤選擇
        if (values.tagSelection && values.tagSelection.length > 0) {
          params.tagIds = values.tagSelection;
        }
        
        // 處理排除標籤選項
        if (values.excludeTagSelection) {
          params.excludeTags = true;
        }
        
        message.loading(t('正在生成報告，請稍候...'), 0);
        
        // 調用API生成報告
        const { blob, fileName } = await apiService.generatePickupDateWaybillListReport(params);
        
        // 下載文件
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(href);
        
        message.destroy();
        message.success(t('報告生成成功'));
      } else if (reportType === 'hktvmall') {
        // 新增的 HKTVmall 訂單摘要報告邏輯
        const params: any = {};

        // 處理日期範圍
        if (values.dateRange && values.dateRange.length === 2) {
          params.dateStart = values.dateRange[0].format('YYYY-MM-DD');
          params.dateEnd = values.dateRange[1].format('YYYY-MM-DD');
        }

        // 處理 Pickup Date 篩選
        if (values.pickupDateRange && values.pickupDateRange.length === 2) {
          params.pickupDateStart = values.pickupDateRange[0].format('YYYY-MM-DD');
          params.pickupDateEnd = values.pickupDateRange[1].format('YYYY-MM-DD');
        }

        // 處理 Delivery Date 篩選
        if (values.deliveryDateRange && values.deliveryDateRange.length === 2) {
          params.deliveryDateStart = values.deliveryDateRange[0].format('YYYY-MM-DD');
          params.deliveryDateEnd = values.deliveryDateRange[1].format('YYYY-MM-DD');
        }

        // 處理店鋪 ID 選擇
        if (values.storeId && values.storeId.length > 0) {
          params.storeId = values.storeId;
        }

        // 處理標籤選擇
        if (values.tagSelection && values.tagSelection.length > 0) {
          params.tagIds = values.tagSelection;
        }

        // 處理排除標籤選項
        if (values.excludeTagSelection) {
          params.excludeTags = true;
        }

        message.loading(t('正在生成 HKTVmall 訂單摘要報告，請稍候...'), 0);
        
        // 調用API生成報告
        const { blob, fileName } = await apiService.generateHktvmallOrderSummaryReport(params);
        
        // 下載文件
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(href);
        
        message.destroy();
        message.success(t('HKTVmall 訂單摘要報告生成成功'));
      } else if (reportType === 'bundle-movements') {
        const params: any = {};

        if (values.bundlePickupDateRange && values.bundlePickupDateRange.length === 2) {
          params.pickupDateStart = values.bundlePickupDateRange[0].format('YYYY-MM-DD');
          params.pickupDateEnd = values.bundlePickupDateRange[1].format('YYYY-MM-DD');
        }

        if (values.bundleStoreCodes && values.bundleStoreCodes.length > 0) {
          params.storeCodes = values.bundleStoreCodes;
        }

        message.loading(t('正在生成 Bundle Movements 報告，請稍候...'), 0);

        const { blob, fileName } = await apiService.generateBundleMovementsReport(params);

        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(href);

        message.destroy();
        message.success(t('Bundle Movements 報告生成成功'));
      }

      setReportModalVisible(false);
      reportForm.resetFields();
    } catch (error) {
      console.error('生成報告失敗:', error);
      message.destroy();
      message.error(t('生成報告失敗: ') + (error instanceof Error ? error.message : t('未知錯誤')));
    }
  };

  // 處理報告取消
  const handleReportCancel = () => {
    setReportModalVisible(false);
    reportForm.resetFields();
  };

  // 從後端載入出庫訂單
  const mapBackendStatusToZh = (s?: string): OutboundOrderStatus => {
    switch ((s || '').toUpperCase()) {
      case 'PENDING': return '待發貨';
      case 'PROCESSING': return '處理中';
      case 'ALLOCATING': return '配貨中';
      case 'VERIFYING': return '核對中';
      case 'PACKED': return '已包裝';
      case 'PICKUP_PENDING': return '待攬收';
      case 'SHIPPED': return '已發貨';
      case 'RECEIVED': return '已收貨';
      case 'RETURN_REQUESTED': return '申請退貨';
      case 'RETURN_PROCESSING': return '退貨處理中';
      case 'RETURN_COMPLETED': return '退貨完成';
      case 'CANCELLED': return '已取消';
      default: return '待發貨';
    }
  };

  const loadOutboundOrders = async () => {
    try {
      setLoading(true);

      const params = {
        search: filters.searchText || undefined,
        status: filters.status ? mapZhStatusToBackend(filters.status as OutboundOrderStatus) : undefined,
        customerId: filters.customer || undefined,
        salesChannel: filters.salesChannel || undefined,
        pickUpDateStart: filters.pickUpDateRange?.[0] || undefined,
        pickUpDateEnd: filters.pickUpDateRange?.[1] || undefined,
        createdDateStart: filters.createdDateRange?.[0] || undefined,
        createdDateEnd: filters.createdDateRange?.[1] || undefined,
        storeId: filters.storeId.length > 0 ? filters.storeId.join(',') : undefined,
        hasStockIssues: filters.hasStockIssues || undefined,
        runTagId: filters.runTagId.length > 0 ? filters.runTagId.join(',') : undefined,
        itemSku: filters.itemSku || undefined,
        sortField: 'created_at',
        sortOrder: 'desc',
        page: currentPage,
        limit: pageSize
      };

      const resp: any = await apiService.getOutboundOrders(params);
      const rows: any[] = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      const mapped: OutboundOrder[] = rows.map((o: any) => ({
        id: o.id,
        orderNumber: o.outboundNumber || o.outbound_number || o.orderNumber || '-',
        customerName: o.customerName || o.customer_id || '-',
        customerCode: o.customerCode || o.customer_id || '-',
        items: Array.isArray(o.items) ? o.items : [],
        courier: o.courier || o.deliveryCompany || '-',
        receiver: o.receiver || '-',
        countryCode: o.countryCode || 'CN',
        deliveryAddress: o.deliveryAddress || '-',
        deliveryTrackingNumber: o.fedexTrackingNo || o.trackingNumber || o.deliveryTrackingNumber || o.waybillNumber || '',
        waybillNumber: o.fedexTrackingNo || o.waybillNumber || o.waybill_number || o.trackingNumber || o.deliveryTrackingNumber || '',
        deliveryRemark: o.deliveryRemark || '',
        status: mapBackendStatusToZh(o.status),
        remark: o.notes || o.remark,
        createdAt: (typeof o.createdAt === 'string' && o.createdAt) || (typeof o.created_at === 'string' && o.created_at) || new Date().toISOString(),
        updatedAt: (typeof o.updatedAt === 'string' && o.updatedAt) || (typeof o.updated_at === 'string' && o.updated_at) || new Date().toISOString(),
        createdBy: o.createdBy || o.created_by || '-',
        totalValue: Number(o.totalAmount ?? o.total_value ?? 0),
        hasStockIssues: Boolean(o.hasStockIssues || false),
        returnInfo: undefined,
        pickUpDate: o.pickUpDate || undefined,
        // FedEx fields
        deliveryProvider: o.deliveryProvider || '',
        fedexTrackingNo: o.fedexTrackingNo || '',
        fedexLabelPdf: o.fedexLabelPdf || null,
        hasFedExLabel: Boolean(o.hasFedExLabel), // From list response
        // Shipping address fields (from Shopify)
        receiverPhone: o.receiverPhone || '',
        postalCode: o.postalCode || '',
        city: o.city || '',
        province: o.province || '',
      }));
      setOutboundOrders(mapped);
      setFilteredOrders(mapped);
      const respTotal = resp?.pagination?.total ?? resp?.total ?? resp?.totalCount ?? resp?.count ?? resp?.meta?.total ?? resp?.meta?.pagination?.total ?? mapped.length;
      setTotal(Number(respTotal) || mapped.length);
    } catch (e) {
      console.error('載入出庫訂單失敗:', e);
      message.error(t('載入出庫訂單失敗'));
    } finally {
      setLoading(false);
    }
  };

  // 將中文狀態映射回後端狀態碼
  const mapZhStatusToBackend = (s: OutboundOrderStatus): string => {
    switch (s) {
      case '待發貨': return 'PENDING';
      case '處理中': return 'PROCESSING';
      case '配貨中': return 'ALLOCATING';
      case '核對中': return 'VERIFYING';
      case '已包裝': return 'PACKED';
      case '待攬收': return 'PICKUP_PENDING';
      case '已發貨': return 'SHIPPED';
      case '已收貨': return 'RECEIVED';
      case '申請退貨': return 'RETURN_REQUESTED';
      case '退貨處理中': return 'RETURN_PROCESSING';
      case '退貨完成': return 'RETURN_COMPLETED';
      case '已取消': return 'CANCELLED';
      default: return 'PENDING';
    }
  };

  useEffect(() => {
    loadOutboundOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filters.searchText, filters.status, filters.customer, filters.salesChannel, filters.hasStockIssues, filters.pickUpDateRange, filters.createdDateRange, filters.storeId, filters.runTagId, filters.itemSku]);

  // 統計數據
  const statistics = React.useMemo(() => {
    const total = filteredOrders.length;
    const pending = filteredOrders.filter(order => order.status === '待發貨').length;
    const processing = filteredOrders.filter(order =>
        ['處理中', '配貨中', '核對中', '已包裝', '待攬收'].includes(order.status)
    ).length;
    const shipped = filteredOrders.filter(order => order.status === '已發貨').length;
    const stockIssues = filteredOrders.filter(order => order.hasStockIssues).length;

    return { total, pending, processing, shipped, stockIssues };
  }, [filteredOrders]);

  // 受控分頁改為後端查詢：前端不再重覆過濾，直接使用後端返回的結果
  useEffect(() => {
    setFilteredOrders(outboundOrders);
  }, [outboundOrders]);

  // 獲取狀態標籤顏色
  const getStatusColor = (status: OutboundOrderStatus) => {
    switch (status) {
      case '待發貨':
        return 'orange';
      case '處理中':
        return 'blue';
      case '配貨中':
        return 'cyan';
      case '核對中':
        return 'purple';
      case '已包裝':
        return 'magenta';
      case '待攬收':
        return 'gold';
      case '已發貨':
        return 'green';
      case '已收貨':
        return 'success';
      case '申請退貨':
        return 'volcano';
      case '退貨處理中':
        return 'red';
      case '退貨完成':
        return 'default';
      case '已取消':
        return 'error';
      default:
        return 'default';
    }
  };

  // 獲取狀態圖標
  const getStatusIcon = (status: OutboundOrderStatus) => {
    switch (status) {
      case '待發貨':
        return <ClockCircleOutlined />;
      case '處理中':
        return <EditOutlined />;
      case '配貨中':
        return <ShoppingCartOutlined />;
      case '核對中':
        return <EyeOutlined />;
      case '已包裝':
        return <InboxOutlined />;
      case '待攬收':
        return <TruckOutlined />;
      case '已發貨':
        return <CheckCircleOutlined />;
      case '已收貨':
        return <CheckCircleOutlined />;
      case '申請退貨':
        return <WarningOutlined />;
      case '退貨處理中':
        return <EditOutlined />;
      case '退貨完成':
        return <CheckCircleOutlined />;
      case '已取消':
        return <CloseCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  // 處理新增訂單
  const handleAddOrder = () => {
    setEditingOrder(null);
    setFormVisible(true);
  };

  // 處理查看訂單詳情
  const handleViewOrder = async (order: OutboundOrder) => {
    try {
      // 調用 detail API 獲取完整數據
      const response: any = await apiService.getOutboundOrder(order.id);
      const orderDetail = response?.data || response;

      if (orderDetail && orderDetail.id) {
        const mappedOrder: OutboundOrder = {
          id: orderDetail.id,
          orderNumber: orderDetail.orderNumber || orderDetail.outboundNumber,
          customerName: orderDetail.customerName || orderDetail.customerCode,
          customerCode: orderDetail.customerCode,
          items: Array.isArray(orderDetail.items) ? orderDetail.items : [],
          courier: orderDetail.courier || orderDetail.deliveryCompany,
          receiver: orderDetail.receiver,
          countryCode: orderDetail.countryCode || 'CN',
          deliveryAddress: orderDetail.deliveryAddress,
          deliveryTrackingNumber: orderDetail.deliveryTrackingNumber || orderDetail.trackingNumber || orderDetail.waybillNumber || orderDetail.waybill_no || orderDetail.fedexTrackingNo,
          waybillNumber: orderDetail.waybillNumber || orderDetail.waybill_no || orderDetail.fedexTrackingNo || orderDetail.deliveryTrackingNumber || '',
          deliveryRemark: orderDetail.deliveryRemark || '',
          status: orderDetail.status ? mapBackendStatusToZh(orderDetail.status) : order.status,
          remark: orderDetail.remark,
          createdAt: orderDetail.createdAt,
          updatedAt: orderDetail.updatedAt,
          createdBy: orderDetail.createdBy,
          totalValue: Number(orderDetail.totalValue ?? orderDetail.total_amount ?? 0),
          orderDiscount: Number(orderDetail.orderDiscount ?? 0),
          hasStockIssues: Boolean(orderDetail.hasStockIssues),
          returnInfo: order.returnInfo,
          pickUpDate: orderDetail.pickUpDate || order.pickUpDate,
          salesChannel: orderDetail.salesChannel || order.salesChannel,
          // FedEx fields
          deliveryProvider: orderDetail.deliveryProvider || '',
          fedexTrackingNo: orderDetail.fedexTrackingNo || '',
          fedexLabelPdf: orderDetail.fedexLabelPdf || null,
          // SF Express fields
          sfExpressLabelPdf: orderDetail.sfExpressLabelPdf || null,
        };
        setSelectedOrder(mappedOrder);
      } else {
        // 降級處理：使用列表數據
        setSelectedOrder(order);
      }
      setDrawerVisible(true);
    } catch (error) {
      console.error('Failed to load order details:', error);
      // 降級處理：使用列表數據
      setSelectedOrder(order);
      setDrawerVisible(true);
    }
  };

  // 處理編輯訂單
  const handleEditOrder = (order: OutboundOrder) => {
    setEditingOrder(order);
    setFormVisible(true);
  };

  // 處理表單提交
  const handleFormSubmit = async (formData: any, confirmOversell: boolean = false, oversellMode?: OversellMode) => {
    try {
      if (editingOrder) {
        // 調用後端更新
        const payload: any = {
          customerCode: formData.customerCode,
          receiver: formData.receiver,
          deliveryAddress: formData.deliveryAddress,
          courier: formData.courier,
          deliveryTrackingNumber: formData.deliveryTrackingNumber,
          status: formData.status,
          remark: formData.remark,
          deliveryRemark: formData.deliveryRemark,
          otherRemarks: formData.otherRemarks,
          totalValue: formData.totalValue,
          items: formData.items,
          warehouseId: formData.warehouseId,
          createdAt: formData.createdAt,
        };

        // 如果用戶已確認超售，添加 confirmOversell 標記與模式（實物足夠=校正 / 實物不足=欠貨）
        if (confirmOversell) {
          payload.confirmOversell = true;
          payload.oversellMode = oversellMode || 'SHORTAGE';
        }

        const resp: any = await apiService.updateOutboundOrder(editingOrder.id || editingOrder.orderNumber, payload);

        // 檢查是否需要確認超售（庫存不足）
        if (resp?.needConfirmation && resp?.stockWarnings) {
          const { warning, insufficientItems } = resp.stockWarnings;
          // 顯示確認對話框（實物足夠=校正並全數出貨 / 實物不足=記入超售欠貨）
          showOversellConfirmDialog(t, warning, insufficientItems, async (mode) => {
            await handleFormSubmit(formData, true, mode);
          });
          return; // 等待用戶確認
        }

        // 回顯：以後端返回詳情替換當前行
        const updated = resp?.data || resp;
        if (updated && updated.id) {
          const mapped: OutboundOrder = {
            id: updated.id,
            orderNumber: updated.orderNumber || updated.outboundNumber || editingOrder.orderNumber,
            customerName: updated.customerName || editingOrder.customerName,
            customerCode: updated.customerCode || editingOrder.customerCode,
            items: Array.isArray(updated.items) ? updated.items : editingOrder.items,
            courier: updated.courier || updated.deliveryCompany || editingOrder.courier,
            receiver: updated.receiver || editingOrder.receiver,
            countryCode: updated.countryCode || editingOrder.countryCode,
            deliveryAddress: updated.deliveryAddress || editingOrder.deliveryAddress,
            deliveryTrackingNumber: updated.deliveryTrackingNumber || updated.trackingNumber || editingOrder.deliveryTrackingNumber,
            deliveryRemark: updated.deliveryRemark ?? editingOrder.deliveryRemark ?? '',
            status: updated.status ? mapBackendStatusToZh(updated.status) : editingOrder.status,
            remark: updated.remark || editingOrder.remark,
            createdAt: updated.createdAt || editingOrder.createdAt,
            updatedAt: updated.updatedAt || new Date().toISOString(),
            createdBy: updated.createdBy || editingOrder.createdBy,
            totalValue: Number(updated.totalValue ?? updated.total_amount ?? editingOrder.totalValue ?? 0),
            hasStockIssues: Boolean(updated.hasStockIssues ?? editingOrder.hasStockIssues),
            returnInfo: editingOrder.returnInfo,
            pickUpDate: updated.pickUpDate || editingOrder.pickUpDate,
          };
          setOutboundOrders(prev => prev.map(o => (o.id === mapped.id ? mapped : o)));
          message.success(t('出庫訂單已更新'));

          // 顯示庫存警告（如果有）
          if (resp?.stockWarnings) {
            message.warning(resp.stockWarnings.warning || t('部分產品庫存不足，已完成超售處理'));
          }
        }
        setFormVisible(false);
        setEditingOrder(null);
      } else {
        // 調用後端新增
        const payload = {
          customerCode: formData.customerCode,
          receiver: formData.receiver,
          deliveryAddress: formData.deliveryAddress,
          courier: formData.courier,
          deliveryTrackingNumber: formData.deliveryTrackingNumber,
          status: formData.status,
          remark: formData.remark,
          deliveryRemark: formData.deliveryRemark,
          otherRemarks: formData.otherRemarks,
          totalValue: formData.totalValue,
          items: formData.items,
          warehouseId: formData.warehouseId,
        };
        const resp: any = await apiService.createOutboundOrder(payload);
        const created = resp?.data || resp;
        if (created && created.id) {
          const mapped: OutboundOrder = {
            id: created.id,
            orderNumber: created.orderNumber || created.outboundNumber,
            customerName: created.customerName || formData.customerName,
            customerCode: created.customerCode || formData.customerCode,
            items: Array.isArray(created.items) ? created.items : formData.items,
            courier: created.courier || created.deliveryCompany || formData.courier,
            receiver: created.receiver || formData.receiver,
            countryCode: created.countryCode || formData.countryCode,
            deliveryAddress: created.deliveryAddress || formData.deliveryAddress,
            deliveryTrackingNumber: created.deliveryTrackingNumber || created.trackingNumber || formData.deliveryTrackingNumber,
            deliveryRemark: created.deliveryRemark || created.remark || formData.deliveryRemark,
            status: mapBackendStatusToZh(created.status) || formData.status,
            remark: created.remark || formData.remark,
            createdAt: created.createdAt || new Date().toISOString(),
            updatedAt: created.updatedAt || new Date().toISOString(),
            createdBy: created.createdBy || 'current_user',
            totalValue: Number(created.totalValue ?? created.total_amount ?? formData.totalValue ?? 0),
            hasStockIssues: Boolean(created.hasStockIssues ?? false),
            returnInfo: undefined,
            pickUpDate: created.pickUpDate || undefined,
          };
          setOutboundOrders(prev => [mapped, ...prev]);
          message.success(t('出庫訂單已建立'));
        }
        setFormVisible(false);
      }
    } catch (error) {
      console.error('Failed to submit outbound order:', error);
      message.error(t('提交出庫訂單失敗'));
    }
  };

  // 處理訂單刪除
  const handleDeleteOrder = async (order: OutboundOrder) => {
    try {
      // 單次刪除：嚴格使用主鍵 ID（後端同時支持 id 或 outbound_number）
      const ident = order.id;
      const resp: any = await apiService.deleteOutboundOrder(ident);
      if (resp && typeof resp === 'object' && 'success' in resp && resp.success === false) {
        throw new Error(resp.message || t('Delete failed'));
      }
      await loadOutboundOrders();
      message.success(t('出庫訂單刪除成功'));
    } catch (error: any) {
      console.error('刪除出庫訂單失敗:', error);
      // 若後端回覆已不存在，視為最終態一致，刷新列表並提示成功
      if (String(error?.message || '').toLowerCase().includes('outbound order not found')) {
        await loadOutboundOrders();
        message.success(t('出庫訂單刪除成功'));
        return;
      }
      message.error(error?.message || t('刪除失敗，請重試'));
    }
  };

  // 導出功能 - 後端生成，帶當前篩選條件且全量導出
  const handleExportExcel = async () => {
    try {
      message.loading(t('正在根據條件導出，請稍候...'), 0.6);
      
      // 確保導出使用的參數與列表查詢完全一致
      const params: any = {
        search: filters.searchText || undefined,
        status: filters.status ? mapZhStatusToBackend(filters.status as OutboundOrderStatus) : undefined,
        salesChannel: filters.salesChannel || undefined,
        pickUpDateStart: filters.pickUpDateRange?.[0] || undefined,
        pickUpDateEnd: filters.pickUpDateRange?.[1] || undefined,
        createdDateStart: filters.createdDateRange?.[0] || undefined,
        createdDateEnd: filters.createdDateRange?.[1] || undefined,
        storeId: filters.storeId.length > 0 ? filters.storeId.join(',') : undefined,
        hasStockIssues: filters.hasStockIssues || undefined,
        runTagId: filters.runTagId.length > 0 ? filters.runTagId.join(',') : undefined,
        sortField: 'created_at',
        sortOrder: 'desc'
      };
      
      // 調試：打印導出參數
      console.log('📤 導出參數:', params);
      console.log('📋 當前篩選條件:', filters);
      
      const { blob, fileName } = await apiService.exportOutboundOrdersCSV(params);
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
      message.success(t('出庫訂單已按當前條件導出'));
    } catch (error) {
      console.error('導出失敗:', error);
      message.error(t('導出失敗，請稍後重試'));
    }
  };

  // Renders a warning modal listing per-store failures for the Update Pick
  // Up Date task. Same shape and colour-coding as the HKTVmall pull page so
  // operators see one consistent error surface.
  const showUpdatePickupDateErrorsModal = (opts: {
    range: string;
    totalAccounts: number;
    successfulAccounts: number;
    failedAccounts: number;
    accountErrors: Array<{
      storeCode: string;
      errorType: 'auth' | 'api' | 'rate_limit' | 'unknown';
      message: string;
      occurredAt: string;
    }>;
  }) => {
    const typeLabel: Record<string, { label: string; hint: string; color: string }> = {
      auth: { label: t('認證過期 (401)'), hint: t('請到 HKTVmall 帳戶設定重新連線'), color: 'red' },
      rate_limit: { label: t('API 限流 (429)'), hint: t('稍後再試或減少並發'), color: 'orange' },
      api: { label: t('HKTV API 錯誤'), hint: t('可能是暫時性，稍後再試'), color: 'gold' },
      unknown: { label: t('其他錯誤'), hint: t('請查看詳細訊息'), color: 'default' },
    };

    Modal.warning({
      title: t('取貨日期更新完成（含警告）'),
      width: 720,
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>
            {t('日期範圍：')}<Typography.Text code>{opts.range}</Typography.Text>
            <span style={{ marginLeft: 12, color: '#666' }}>
              {t('帳號總數')} {opts.totalAccounts}{t('，成功')} <Typography.Text style={{ color: '#52c41a' }}>{opts.successfulAccounts}</Typography.Text>{t('， 失敗')} <Typography.Text style={{ color: '#ff4d4f' }}>{opts.failedAccounts}</Typography.Text>
            </span>
          </div>
          <div style={{ marginBottom: 8, color: '#666', fontSize: 12 }}>
            {t('這些商店的 HKTV API 呼叫失敗了，該範圍內的訂單取貨日期未同步。錯誤已記錄到 hktv_issues 表。')}
                            </div>
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('商店')}</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('類型')}</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('建議')}</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('訊息')}</th>
                </tr>
              </thead>
              <tbody>
                {opts.accountErrors.map((e, i) => {
                  const meta = typeLabel[e.errorType] || typeLabel.unknown;
                  return (
                    <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{e.storeCode}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <Tag color={meta.color}>{meta.label}</Tag>
                      </td>
                      <td style={{ padding: '6px 8px', color: '#666' }}>{meta.hint}</td>
                      <td style={{ padding: '6px 8px', color: '#999', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.message}>{e.message}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ),
      okText: t('我知道了'),
    });
  };

  // Handler for Update Pick Up Date button
  const handleUpdatePickUpDate = async () => {
    if (!updatePickUpDateRange) {
      message.error(t('請選擇取貨日期範圍'));
      return;
    }

    const startDate = updatePickUpDateRange[0];
    const endDate = updatePickUpDateRange[1];
    
    // Validate max 7 days range
    const daysDiff = endDate.diff(startDate, 'days');
    if (daysDiff > 7) {
      message.error(t('日期範圍不能超過7天'));
      return;
    }

    try {
      setUpdatePickUpDateLoading(true);
      
      // 將所有查詢條件傳遞給 API（與 /api/v1/outbound 接口的參數一致）
      const params: any = {
        pickUpDateStart: startDate.format('YYYY-MM-DD'),
        pickUpDateEnd: endDate.format('YYYY-MM-DD'),
        // 傳遞當前頁面的所有過濾條件
        search: filters.searchText || undefined,
        status: filters.status ? mapZhStatusToBackend(filters.status as OutboundOrderStatus) : undefined,
        warehouseId: undefined, // 如果有倉庫過濾可以在這裡添加
        customerId: undefined, // 如果有客戶過濾可以在這裡添加
        dateFrom: undefined, // 如果有出庫日期過濾可以在這裡添加
        dateTo: undefined,
        createdDateStart: filters.createdDateRange?.[0] || undefined,
        createdDateEnd: filters.createdDateRange?.[1] || undefined,
        storeId: filters.storeId.length > 0 ? filters.storeId.join(',') : undefined,
        hasStockIssues: filters.hasStockIssues || undefined,
        runTagId: filters.runTagId.length > 0 ? filters.runTagId.join(',') : undefined
      };
      
      console.log('Update Pick Up Date with filters:', params);

      // Call backend API - 現在立即返回 taskId
      const response: any = await apiService.updatePickUpDateRange(params);
      
      if (response.success && response.taskId) {
        const taskId = response.taskId;
        
        message.info(`任務已創建，正在後台處理中...`);
        
        // Close modal immediately
        setUpdatePickUpDateModalVisible(false);
        setUpdatePickUpDateRange(null);
        
        // 輪詢檢查任務狀態
        let pollErrorCount = 0;
        const MAX_POLL_ERRORS = 5; // 允許5次連續錯誤

        const pollInterval = setInterval(async () => {
          try {
            console.log(`[輪詢] 檢查任務狀態: ${taskId}`);
            const taskStatus: any = await apiService.getTaskStatus(taskId);

            // 重置錯誤計數
            pollErrorCount = 0;

            console.log(`[輪詢] 收到響應:`, taskStatus);

            if (taskStatus.success && taskStatus.task) {
              const task = taskStatus.task;

              console.log(`[輪詢] 任務狀態: ${task.status}, 進度: ${task.progress}/${task.total}`);

              // 顯示進度信息
              if (task.status === 'running') {
                message.loading({
                  content: `${task.message}`,
                  key: taskId,
                  duration: 0
                });
              } else if (task.status === 'completed') {
                console.log(`[輪詢] ✅ 任務完成！停止輪詢`);
                clearInterval(pollInterval);
                message.destroy(taskId);

                // Task returned successfully overall, but individual stores may
                // still have failed silently (e.g. H9642001 with expired token).
                // The service now returns accountErrors on task.result — surface
                // those to the operator instead of pretending everything worked.
                const result = task.result || {};
                const accountErrors: Array<{
                  storeCode: string;
                  errorType: 'auth' | 'api' | 'rate_limit' | 'unknown';
                  message: string;
                  occurredAt: string;
                }> = Array.isArray(result.accountErrors) ? result.accountErrors : [];

                if (accountErrors.length > 0) {
                  showUpdatePickupDateErrorsModal({
                    range: t('{{start}} 至 {{end}}', { start: params.pickUpDateStart, end: params.pickUpDateEnd }),
                    totalAccounts: Number(result.totalAccounts || 0),
                    successfulAccounts: Number(result.successfulAccounts || 0),
                    failedAccounts: Number(result.failedAccounts || accountErrors.length),
                    accountErrors,
                  });
                } else {
                  message.success(t('取貨日期更新完成！範圍: {{start}} 至 {{end}}', { start: params.pickUpDateStart, end: params.pickUpDateEnd }));
                }

                // Reload orders after completion
                loadOutboundOrders();
              } else if (task.status === 'failed') {
                console.log(`[輪詢] ❌ 任務失敗！停止輪詢`);
                clearInterval(pollInterval);
                message.destroy(taskId);
                message.error(t('任務失敗: {{error}}', { error: task.error || t('未知錯誤') }));
              }
            }
          } catch (error) {
            console.error('[輪詢] ❌ 輪詢出錯:', error);
            pollErrorCount++;

            // 只有在連續多次錯誤后才停止輪詢
            if (pollErrorCount >= MAX_POLL_ERRORS) {
              console.error(`[輪詢] ❌ 連續 ${MAX_POLL_ERRORS} 次錯誤，停止輪詢`);
              clearInterval(pollInterval);
              message.destroy(taskId);
              message.warning(t('無法獲取任務狀態，請刷新頁面查看結果'));
            } else {
              console.warn(`[輪詢] ⚠️ 錯誤 ${pollErrorCount}/${MAX_POLL_ERRORS}，繼續重試...`);
            }
          }
        }, 2000); // 每2秒檢查一次

        // 設置最大輪詢時間（60分鐘）
        setTimeout(() => {
          console.log(`[輪詢] ⏱️ 超時，停止輪詢`);
          clearInterval(pollInterval);
          message.destroy(taskId);
          message.info(t('任務處理時間較長，請刷新頁面查看結果'));
        }, 3600000); // 60分鐘

      } else {
        message.error(t('創建任務失敗'));
      }

    } catch (error: any) {
      console.error('更新取貨日期失敗:', error);

      // 檢查是否是 409 錯誤（已有任務在執行）
      if (error.response?.status === 409) {
        const errorData = error.response.data;
        const currentTask = errorData.currentTask;

        // 顯示警告信息而不是錯誤
        message.warning({
          content: (
            <div>
              <div>{errorData.error}</div>
              {currentTask && (
                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                  {t('當前任務進度:')} {currentTask.message}
                </div>
              )}
            </div>
          ),
          duration: 5
        });

        // 關閉模態框
        setUpdatePickUpDateModalVisible(false);
        setUpdatePickUpDateRange(null);

        // 可選：自動開始輪詢當前任務的進度
        if (errorData.currentTaskId) {
          const currentTaskId = errorData.currentTaskId;
          message.info(t('正在查看當前任務進度...'));

          let pollErrorCount = 0;
          const MAX_POLL_ERRORS = 5;

          const pollInterval = setInterval(async () => {
            try {
              const taskStatus: any = await apiService.getTaskStatus(currentTaskId);

              // 重置錯誤計數
              pollErrorCount = 0;

              if (taskStatus.success && taskStatus.task) {
                const task = taskStatus.task;

                if (task.status === 'running') {
                  message.loading({
                    content: `${task.message}`,
                    key: currentTaskId,
                    duration: 0
                  });
                } else if (task.status === 'completed') {
                  clearInterval(pollInterval);
                  message.destroy(currentTaskId);
                  message.success(t('當前任務已完成，您現在可以發起新的更新任務'));
                  loadOutboundOrders();
                } else if (task.status === 'failed') {
                  clearInterval(pollInterval);
                  message.destroy(currentTaskId);
                  message.error(t('當前任務失敗: {{error}}', { error: task.error || t('未知錯誤') }));
                }
              }
            } catch (err) {
              console.error('輪詢任務狀態失敗:', err);
              pollErrorCount++;

              // 只有在連續多次錯誤后才停止輪詢
              if (pollErrorCount >= MAX_POLL_ERRORS) {
                clearInterval(pollInterval);
                message.destroy(currentTaskId);
                message.warning(t('無法獲取任務狀態，請刷新頁面查看結果'));
              }
            }
          }, 2000);

          // 設置最大輪詢時間（60分鐘）
          setTimeout(() => {
            clearInterval(pollInterval);
            message.destroy(currentTaskId);
            message.info(t('任務處理時間較長，請刷新頁面查看結果'));
          }, 3600000); // 60分鐘
        }
      } else {
        // 其他錯誤顯示錯誤信息
        message.error(t('更新取貨日期失敗，請稍後重試'));
      }
    } finally {
      setUpdatePickUpDateLoading(false);
    }
  };

  // 廢除本地CSV組裝，改由後端生成（保留佔位避免引用錯誤）
  const generateCSVContent = (_data: OutboundOrder[]) => '';

  // 獲取過濾後的出庫訂單數據（用於導出等功能）
  const getFilteredOutboundOrders = () => {
    // sortedAndFilteredOutboundOrders 已經包含了所有篩選邏輯（包括 hasStockIssues）
    // 這裡只需要返回排序後的數據即可
    return sortedAndFilteredOutboundOrders;
  };

  // 處理狀態變更
  const handleStatusChange = async (orderId: string, newStatus: OutboundOrderStatus) => {
    try {
      const target = outboundOrders.find(o => o.id === orderId);
      if (!target) return;
      // Check for tracking number - accept either regular delivery tracking number, FedEx tracking number, or waybill number
      const hasTrackingNumber = target.deliveryTrackingNumber ||
                                target.fedexTrackingNo ||
                                target.waybillNumber;
      if (newStatus !== '待發貨' && !hasTrackingNumber) {
        message.warning(t('請填寫配送單號'));
        return;
      }
      // 已移除 hasStockIssues 檢查 - 改為後端確認流程
      // 後端批量端點也支援單筆，並在處理中時建立批次與關聯（保證與批量行為一致）
      const isProcessing = newStatus === '處理中';

      // 構建 orderWaybillList（單個訂單）
      const orderWaybillList = [{
        orderNumber: target.orderNumber,
        waybillNumber: (target as any).fedexTrackingNo || (target as any).waybillNumber || (target as any).waybill_number || (target as any).trackingNumber || target.deliveryTrackingNumber || '',
        deliveryProvider: (target as any).deliveryProvider || '',
        outboundOrderId: target.id,
        fedexTrackingNo: (target as any).fedexTrackingNo || '',
        hasFedExLabel: !!(target as any).hasFedExLabel  // Use hasFedExLabel from list response
      }];

      const payload: any = {
        ids: [orderId],
        status: isProcessing ? 'processing' : mapZhStatusToBackend(newStatus),
        group: true,
        orderNumbers: [target.orderNumber],
        orderWaybillList: orderWaybillList
      };

      const resp: any = await apiService.batchUpdateOutboundStatus(payload);

      // 檢查是否需要確認超售
      if (resp?.needConfirmation && resp?.stockWarnings) {
        const { warning, insufficientItems } = resp.stockWarnings;

        // 顯示確認對話框（實物足夠=校正並全數出貨 / 實物不足=記入超售欠貨）
        showOversellConfirmDialog(t, warning, insufficientItems, async (mode) => {
            try {
              const hideLoading = message.loading(t('正在處理訂單...'), 0);

              // 帶上 confirmOversell 標記與模式重新調用 API
              const confirmedResp: any = await apiService.batchUpdateOutboundStatus({
                ...payload,
                confirmOversell: true,
                oversellMode: mode
              });

              hideLoading();

              // 重新加載數據
              await loadOutboundOrders();

              if (isProcessing && confirmedResp?.batch?.batchPickingNumber) {
                Modal.success({
                  title: t('已生成批量揀貨單'),
                  content: (
                    <div>
                      <p>{t('批次編號：')}{confirmedResp.batch.batchPickingNumber}</p>
                      <p>{t('已將出庫單')} {target.orderNumber} {t('加入該批次')}</p>
                      <p style={{ color: '#d46b08', marginTop: 8 }}>{t('⚠️ 產品已超售，請儘快補充庫存')}</p>
                    </div>
                  )
                });
              } else {
                message.success(t('訂單狀態已更新為 {{newStatus}}（已超售）', { newStatus: statusLabel(newStatus) }));
              }

              // 【自動打印運單】確認超售後，自動打印運單（單個訂單）
              if (isProcessing) {
                const orderWaybillList = [{
                  orderNumber: target.orderNumber,
                  waybillNumber: (target as any).fedexTrackingNo || (target as any).waybillNumber || (target as any).waybill_number || (target as any).trackingNumber || target.deliveryTrackingNumber || '',
                  deliveryProvider: (target as any).deliveryProvider || '',
                  outboundOrderId: target.id,
                  fedexTrackingNo: (target as any).fedexTrackingNo || '',
                  hasFedExLabel: !!(target as any).hasFedExLabel  // Use hasFedExLabel from list response
                }];
                try {
                  console.log('[OutboundOrderPage] 開始自動打印運單（單個訂單超售確認後）...', { orderWaybillList });
                  await printBatchWaybillsOnProcessing(orderWaybillList, [target.id]);
                  console.log('[OutboundOrderPage] 運單打印完成');
                } catch (printErr) {
                  console.error('[OutboundOrderPage] 運單打印失敗:', printErr);
                  message.warning(t('運單打印失敗，請手動打印'));
                }
              }
            } catch (confirmErr: any) {
              console.error('確認超售後處理失敗:', confirmErr);
              message.error(confirmErr?.response?.data?.error || t('處理失敗'));
            }
        });
        return;
      }

      // 重新加載訂單數據（包括最新庫存狀態）
      await loadOutboundOrders();

      if (isProcessing) {
        const batchNo = resp?.batch?.batchPickingNumber;
        if (batchNo) {
          Modal.success({ title: t('已生成批量揀貨單'), content: (
                <div>
                  <p>{t('批次編號：')}{batchNo}</p>
                  <p>{t('已將出庫單')} {target.orderNumber} {t('加入該批次')}</p>
                </div>
            )});
        }

        // 【自動打印運單】單個訂單狀態變更為處理中時，自動打印運單
        const orderWaybillListForPrint = [{
          orderNumber: target.orderNumber,
          waybillNumber: (target as any).fedexTrackingNo || (target as any).waybillNumber || (target as any).waybill_number || (target as any).trackingNumber || target.deliveryTrackingNumber || '',
          deliveryProvider: (target as any).deliveryProvider || '',
          outboundOrderId: target.id,
          fedexTrackingNo: (target as any).fedexTrackingNo || '',
          hasFedExLabel: !!(target as any).hasFedExLabel  // Use hasFedExLabel from list response
        }];
        try {
          console.log('[OutboundOrderPage] 開始自動打印運單（單個訂單）...', { orderWaybillList: orderWaybillListForPrint });
          await printBatchWaybillsOnProcessing(orderWaybillListForPrint, [target.id]);
          console.log('[OutboundOrderPage] 運單打印完成');
        } catch (printErr) {
          console.error('[OutboundOrderPage] 運單打印失敗:', printErr);
          message.warning(t('運單打印失敗，請手動打印'));
        }
      }
      message.success(t('訂單狀態已更新為 {{newStatus}}', { newStatus: statusLabel(newStatus) }));

      // 顯示庫存警告（如果有，用戶已確認後的提示）
      if (resp?.stockWarnings) {
        const { warning, insufficientItems } = resp.stockWarnings;
        Modal.warning({
          title: t('⚠️ 庫存不足提醒'),
          content: (
            <div>
              <p style={{ marginBottom: 12 }}>{warning}</p>
              <p style={{ marginBottom: 8 }}><strong>{t('以下產品庫存不足：')}</strong></p>
              <div style={{ maxHeight: '200px', overflow: 'auto', background: '#fffbe6', padding: 12, borderRadius: 4 }}>
                {insufficientItems?.map((item: any, idx: number) => (
                  <div key={idx} style={{ marginBottom: 8 }}>
                    <p style={{ margin: 0 }}><strong>{t('SKU:')}</strong> {item.sku}</p>
                    <p style={{ margin: 0, color: '#d46b08' }}>{t('需要')} {item.required} {t('個，可用')} {item.available} {t('個')}</p>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: 12, color: '#8c8c8c', fontSize: 12 }}>
                {t('訂單已成功變更，請注意補充庫存。')}
                                    </p>
            </div>
          ),
          width: 500
        });
      }
    } catch (e) {
      console.error(e);
      const errorMessage = e?.response?.data?.error || e?.message || t('更新失敗');
      const errorDetails = e?.response?.data?.details || '';

      if (errorDetails) {
        Modal.error({
          title: t('更新失敗'),
          content: (
            <div>
              <p>{errorMessage}</p>
              <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>{errorDetails}</pre>
            </div>
          ),
          width: 600
        });
      } else {
        message.error(errorMessage);
      }
    }
  };

  // 批量更新狀態
  const handleBatchStatusChange = async (newStatus: OutboundOrderStatus) => {
    try {
      if (!selectedRowKeys || selectedRowKeys.length === 0) {
        message.warning(t('請先選擇要更新的訂單'));
        return;
      }
      
      // 防止重複點擊
      if (loading) {
        message.warning(t('正在處理中，請稍候...'));
        return;
      }

      const isProcessing = newStatus === '處理中';

      // 過濾掉終態訂單（已收貨、已取消、退貨相關），這些訂單不能更改狀態
      const terminalStatuses = ['已收貨', '已取消', '退貨處理中', '退貨完成'];
      const allSelectedOrders = outboundOrders.filter(o => selectedRowKeys.includes(o.id));
      const terminalOrders = allSelectedOrders.filter(o => terminalStatuses.includes(o.status) || o.status?.includes('退貨'));
      const validOrders = allSelectedOrders.filter(o => !terminalStatuses.includes(o.status) && !o.status?.includes('退貨'));

      if (validOrders.length === 0) {
        message.warning(t('所選訂單均為終態（已收貨/已取消/退貨），無法更改狀態'));
        return;
      }

      if (terminalOrders.length > 0) {
        message.info(t('已自動排除 {{excluded}} 筆終態訂單（已收貨/已取消/退貨），將處理剩餘 {{remaining}} 筆訂單', { excluded: terminalOrders.length, remaining: validOrders.length }));
      }

      // 轉換為字符串數組（只包含可更改狀態的訂單）
      const ids = validOrders.map(o => String(o.id));

      // 收集所有選中訂單的 orderNumber 和 waybillNumber
      const selectedOrders = validOrders;
      const orderNumbers = selectedOrders.map(o => o.orderNumber);
      
      // 構建 orderNumber 和 waybillNumber 的鍵值對列表
      // 使用與第 1014 行相同的映射邏輯，確保能取到運單號（支持多種字段名）
      // 包含 deliveryProvider, outboundOrderId, fedexTrackingNo, hasFedExLabel 以支援 FedEx 標籤打印
      const orderWaybillList = selectedOrders.map(o => ({
        orderNumber: o.orderNumber,
        waybillNumber: (o as any).fedexTrackingNo || (o as any).waybillNumber || (o as any).waybill_number || (o as any).trackingNumber || o.deliveryTrackingNumber || '',
        deliveryProvider: (o as any).deliveryProvider || '',
        outboundOrderId: o.id,
        fedexTrackingNo: (o as any).fedexTrackingNo || '',
        hasFedExLabel: !!(o as any).hasFedExLabel  // Use hasFedExLabel from list response
      }));
      
      // 顯示確認對話框
      Modal.confirm({
        title: t('確認批量更新狀態'),
        content: (
          <div>
            <p>{t('確定要將選中的')} <strong>{ids.length}</strong> {t('筆訂單狀態更新為「')}<strong>{statusLabel(newStatus)}</strong>{t('」嗎？')}</p>
            {ids.length > 100 && (
              <p style={{ color: '#ff4d4f', marginTop: 8 }}>
                {t('⚠️ 訂單數量較多，處理可能需要一些時間，請耐心等待...')}
                                      </p>
            )}
          </div>
        ),
        okText: t('確定'),
        cancelText: t('取消'),
        okButtonProps: { danger: isProcessing },
        onOk: async () => {
          try {
            // 設置加載狀態
            setLoading(true);

            const hideLoading = message.loading(t('正在批量更新 {{count}} 筆訂單狀態...', { count: ids.length }), 0);

            // 構建請求 payload
            const payload: any = {
              ids: ids,
              status: isProcessing ? 'processing' : mapZhStatusToBackend(newStatus),
              group: isProcessing,
              orderNumbers: orderNumbers,
              orderWaybillList: orderWaybillList
            };

            // 調用後端批量更新 API
            let resp: any = await apiService.batchUpdateOutboundStatus(payload);

            // 隱藏加載提示
            hideLoading();

            // 檢查是否需要確認超售
            if (resp?.needConfirmation && resp?.stockWarnings) {
              const { warning, insufficientItems } = resp.stockWarnings;

              // 顯示確認對話框（實物足夠=校正並全數出貨 / 實物不足=記入超售欠貨）
              showOversellConfirmDialog(t, warning, insufficientItems, async (mode) => {
                  try {
                    const confirmHideLoading = message.loading(t('正在處理訂單...'), 0);

                    // 帶上 confirmOversell 標記與模式重新調用 API
                    const confirmedResp: any = await apiService.batchUpdateOutboundStatus({
                      ...payload,
                      confirmOversell: true,
                      oversellMode: mode
                    });

                    confirmHideLoading();

                    // 清空選擇
                    setSelectedRowKeys([]);

                    // 重新加載數據
                    await loadOutboundOrders();

                    // 顯示成功消息
                    if (isProcessing && confirmedResp?.batch?.batchPickingNumber) {
                      Modal.success({
                        title: t('已生成批量揀貨單'),
                        content: (
                          <div>
                            <p>{t('批次編號：')}<strong>{confirmedResp.batch.batchPickingNumber}</strong></p>
                            <p>{t('共')} <strong>{ids.length}</strong> {t('張出庫單')}</p>
                            <p style={{ color: '#d46b08', marginTop: 8 }}>{t('⚠️ 部分產品已超售，請儘快補充庫存')}</p>
                          </div>
                        )
                      });
                    } else {
                      message.success(t('✅ 已批量更新 {{count}} 筆狀態為「{{status}}」（含超售訂單）', { count: ids.length, status: statusLabel(newStatus) }));
                    }

                    // 【自動打印運單】確認超售後，自動打印所有運單
                    if (isProcessing) {
                      try {
                        console.log('[OutboundOrderPage] 開始自動打印運單（超售確認後）...', { orderWaybillList, ids });
                        await printBatchWaybillsOnProcessing(orderWaybillList, ids);
                        console.log('[OutboundOrderPage] 運單打印完成');
                      } catch (printErr) {
                        console.error('[OutboundOrderPage] 運單打印失敗:', printErr);
                        message.warning(t('運單打印失敗，請手動打印'));
                      }
                    }
                  } catch (confirmErr: any) {
                    console.error('確認超售後處理失敗:', confirmErr);
                    message.error(confirmErr?.response?.data?.error || t('處理失敗'));
                  } finally {
                    setLoading(false);
                  }
              }, () => { setLoading(false); });
              return; // 等待用戶確認，不繼續執行下面的代碼
            }

            // 清空選擇
            setSelectedRowKeys([]);

            // 重新加載當前頁數據（從後端獲取最新狀態）
            await loadOutboundOrders();

            // 顯示成功消息
            if (isProcessing && resp?.batch?.batchPickingNumber) {
              Modal.success({
                title: t('已生成批量揀貨單'),
                content: (
                  <div>
                    <p>{t('批次編號：')}<strong>{resp.batch.batchPickingNumber}</strong></p>
                    <p>{t('共')} <strong>{ids.length}</strong> {t('張出庫單')}</p>
                  </div>
                )
              });
            } else {
              message.success(t('✅ 已批量更新 {{count}} 筆狀態為「{{status}}」', { count: ids.length, status: statusLabel(newStatus) }));
            }

            // 【自動打印運單】狀態變更為處理中時，自動打印所有運單
            if (isProcessing) {
              try {
                console.log('[OutboundOrderPage] 開始自動打印運單...', { orderWaybillList, ids });
                await printBatchWaybillsOnProcessing(orderWaybillList, ids);
                console.log('[OutboundOrderPage] 運單打印完成');
              } catch (printErr) {
                console.error('[OutboundOrderPage] 運單打印失敗:', printErr);
                message.warning(t('運單打印失敗，請手動打印'));
              }
            }

            // 顯示庫存警告（如果有，用戶已確認後的提示）
            if (resp?.stockWarnings) {
              const { warning, insufficientItems } = resp.stockWarnings;
              Modal.warning({
                title: t('⚠️ 庫存不足提醒'),
                content: (
                  <div>
                    <p style={{ marginBottom: 12 }}>{warning}</p>
                    <p style={{ marginBottom: 8 }}><strong>{t('以下產品庫存不足：')}</strong></p>
                    <div style={{ maxHeight: '300px', overflow: 'auto', background: '#fffbe6', padding: 12, borderRadius: 4 }}>
                      {insufficientItems?.map((item: any, idx: number) => (
                        <div key={idx} style={{ marginBottom: 8, borderBottom: idx < insufficientItems.length - 1 ? '1px solid #ffe58f' : 'none', paddingBottom: 8 }}>
                          <p style={{ margin: 0 }}><strong>{t('SKU:')}</strong> {item.sku}</p>
                          <p style={{ margin: 0, color: '#d46b08' }}>{t('需要')} <strong>{item.required}</strong> {t('個，可用')} <strong>{item.available}</strong> {t('個（缺 {{count}} 個）', { count: item.required - item.available })}</p>
                          <p style={{ margin: 0, fontSize: 12, color: '#999' }}>{t('影響訂單：')}{Array.isArray(item.orders) ? item.orders.slice(0, 5).join(', ') : ''}{(item.totalOrderCount || item.orders?.length || 0) > 5 ? ' ' + t('...等 {{count}} 張', { count: item.totalOrderCount || item.orders?.length }) : ''}</p>
                        </div>
                      ))}
                    </div>
                    <p style={{ marginTop: 12, color: '#8c8c8c', fontSize: 12 }}>
                      {t('訂單已成功變更為處理中，請注意補充庫存以避免出貨延遲。')}
                                                </p>
                  </div>
                ),
                width: 600
              });
            }
          } catch (e) {
            console.error('批量更新失敗:', e);
            const errorMessage = e?.response?.data?.error || e?.message || t('批量更新失敗');
            const errorDetails = e?.response?.data?.details || '';

            if (errorDetails) {
              Modal.error({
                title: t('批量更新失敗'),
                content: (
                  <div>
                    <p>{errorMessage}</p>
                    <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto' }}>{errorDetails}</pre>
                  </div>
                ),
                width: 700
              });
            } else {
              message.error(errorMessage);
            }
          } finally {
            setLoading(false);
          }
        }
      });
    } catch (e) {
      console.error('批量更新操作失敗:', e);
      message.error(t('批量更新操作失敗'));
    }
  };

  // 批量狀態下拉菜單（與篩選區分的操作 UI）
  const batchStatusMenu = (
      <Menu onClick={({ key }) => handleBatchStatusChange(key as OutboundOrderStatus)}>
        <Menu.Item key="待發貨">{t('待發貨')}</Menu.Item>
        <Menu.Item key="處理中">{t('處理中')}</Menu.Item>
        <Menu.Item key="配貨中">{t('配貨中')}</Menu.Item>
        <Menu.Item key="核對中">{t('核對中')}</Menu.Item>
        <Menu.Item key="已包裝">{t('已包裝')}</Menu.Item>
        <Menu.Item key="待攬收">{t('待攬收')}</Menu.Item>
        <Menu.Item key="已發貨">{t('已發貨')}</Menu.Item>
        <Menu.Item key="已收貨">{t('已收貨')}</Menu.Item>
        <Menu.Item key="已取消">{t('已取消')}</Menu.Item>
      </Menu>
  );


  // 批量狀態更新
  // const handleBatchStatusUpdate = (_orderIds: string[], _newStatus: OutboundOrderStatus) => {
  //   // 保留函數簽名以便後續實作，暫不使用
  // };

  // 處理退貨申請
  const handleReturnRequest = (order: OutboundOrder) => {
    if (order.status !== '已收貨') {
      message.warning(t('只有已收貨的訂單才能申請退貨'));
      return;
    }
    setReturningOrder(order);
    setReturnModalVisible(true);
  };

  // 提交退貨處理
  const handleReturnSubmit = (orderId: string, returnInfo: any) => {
    setOutboundOrders(prev => prev.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          status: '退貨完成' as OutboundOrderStatus,
          returnInfo: returnInfo,
          updatedAt: new Date().toISOString()
        };
      }
      return order;
    }));

    // 如果選擇保留商品且已確認庫存調整，模擬庫存更新
    if (returnInfo.handlingStatus === 'keep' && returnInfo.inventoryAdjusted) {
      Modal.success({
        title: t('庫存已更新'),
        content: (
            <div>
              <p>{t('退貨商品已成功補回庫存。')}</p>
              <p>{t('訂單號:')} {outboundOrders.find(o => o.id === orderId)?.orderNumber}</p>
              <p>{t('更新時間:')} {new Date().toLocaleString()}</p>
            </div>
        ),
      });
    }

    setReturnModalVisible(false);
    setReturningOrder(null);
  };

  // 處理批量上傳完成
  const handleBulkUploadComplete = async (uploadedOrders: any[]) => {
    try {
      const result: any = await apiService.bulkCreateOutboundOrders(uploadedOrders);
      
      // 關閉模態框
      setBulkUploadVisible(false);
      
      // 顯示成功消息
      if (result?.data && Array.isArray(result.data)) {
        message.success(t('成功導入 {{count}} 個訂單', { count: result.data.length }));
      } else {
        message.success(t('批量導入完成'));
      }
      
      // 重置到第一頁並重新加載數據
      setCurrentPage(1);
      await loadOutboundOrders(1, pageSize);
    } catch (e) {
      console.error('批量導入失敗', e);
      message.error(t('批量導入失敗，請重試'));
      setBulkUploadVisible(false);
    }
  };

  // 定義所有可用的出庫訂單列
  const allOutboundColumns = {
    orderNumber: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('orderNumber')}
          >
            {t('訂單號')}
                            {getOutboundSortIcon('orderNumber')}
          </div>
      ),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 150,
      sorter: true,
      render: (orderNumber: string, record: OutboundOrder) => (
          <div>
            <Text strong style={{ color: record.hasStockIssues ? '#ff4d4f' : undefined }}>
              {orderNumber}
            </Text>
            {record.hasStockIssues && (
                <Tooltip title={t('庫存不足')}>
                  <WarningOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} />
                </Tooltip>
            )}
          </div>
      ),
    },
    customerName: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('customerName')}
          >
            {t('客戶名稱')}
                            {getOutboundSortIcon('customerName')}
          </div>
      ),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 160,
      sorter: true,
      render: (text: string) => <Text strong>{text}</Text>,
    },
    customerCode: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('customerCode')}
          >
            {t('客戶代碼')}
                            {getOutboundSortIcon('customerCode')}
          </div>
      ),
      dataIndex: 'customerCode',
      key: 'customerCode',
      width: 120,
      sorter: true,
      render: (text: string) => <Text type="secondary">{text}</Text>,
    },
    receiver: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('receiver')}
          >
            {t('收件人')}
                            {getOutboundSortIcon('receiver')}
          </div>
      ),
      key: 'receiver',
      width: 120,
      sorter: true,
      render: (_: any, record: OutboundOrder) => (
          <div>
            <UserOutlined style={{ marginRight: 4 }} />
            {record.receiver}
          </div>
      ),
    },
    deliveryAddress: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('deliveryAddress')}
          >
            {t('配送地址')}
                            {getOutboundSortIcon('deliveryAddress')}
          </div>
      ),
      dataIndex: 'deliveryAddress',
      key: 'deliveryAddress',
      sorter: true,
      ellipsis: {
        showTitle: false,
      },
      render: (address: string) => (
          <Tooltip placement="topLeft" title={address}>
            {address}
          </Tooltip>
      ),
    },
    courier: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('courier')}
          >
            {t('快遞商')}
                            {getOutboundSortIcon('courier')}
          </div>
      ),
      dataIndex: 'courier',
      key: 'courier',
      width: 100,
      sorter: true,
      render: (courier: string) => (
          <Tag icon={<TruckOutlined />}>{courier}</Tag>
      ),
    },
    salesChannel: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('salesChannel')}
          >
            {t('銷售渠道')}
                            {getOutboundSortIcon('salesChannel')}
          </div>
      ),
      dataIndex: 'salesChannel',
      key: 'salesChannel',
      width: 120,
      sorter: true,
      render: (salesChannel: string) => {
          const channelConfig: Record<string, { label: string; color: string }> = {
              'HKTVMALL': { label: t('HKTVmall'), color: 'orange' },
              'SHOPIFY': { label: t('Shopify'), color: 'green' },
              'MANUAL': { label: t('手動建立'), color: 'blue' },
          };
          const config = channelConfig[salesChannel] || { label: salesChannel || '-', color: 'default' };
          return salesChannel ? (
              <Tag color={config.color} icon={<GlobalOutlined />}>{config.label}</Tag>
          ) : '-';
      },
    },
    pickUpDate: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('pickUpDate')}
          >
            {t('取貨日期')}
                            {getOutboundSortIcon('pickUpDate')}
          </div>
      ),
      dataIndex: 'pickUpDate',
      key: 'pickUpDate',
      width: 120,
      sorter: true,
      render: (pickUpDate: string) => (
          pickUpDate ? (
              <div>
                <div>{dayjs.utc(pickUpDate).tz('Asia/Hong_Kong').format('YYYY-MM-DD')}</div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {dayjs.utc(pickUpDate).tz('Asia/Hong_Kong').format('HH:mm')}
                </Text>
              </div>
          ) : '-'
      ),
    },
    trackingNumber: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('deliveryTrackingNumber')}
          >
            {t('追蹤號')}
                            {getOutboundSortIcon('deliveryTrackingNumber')}
          </div>
      ),
      dataIndex: 'deliveryTrackingNumber',
      key: 'deliveryTrackingNumber',
      width: 120,
      sorter: true,
      render: (trackingNumber: string) => (
          trackingNumber ? (
              <Text copyable={{ text: trackingNumber }}>
                {trackingNumber}
              </Text>
          ) : '-'
      ),
    },
    waybill: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('waybillNumber')}
          >
            {t('運單號')}
                            {getOutboundSortIcon('waybillNumber')}
          </div>
      ),
      dataIndex: 'waybillNumber',
      key: 'waybillNumber',
      width: 140,
      sorter: true,
      render: (wb: string) => (
          wb ? <Text copyable={{ text: wb }}>{wb}</Text> : '-'
      ),
    },
    status: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('status')}
          >
            {t('狀態')}
                            {getOutboundSortIcon('status')}
          </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      sorter: true,
      filters: [
        { text: t('待發貨'), value: '待發貨' },
        { text: t('處理中'), value: '處理中' },
        { text: t('配貨中'), value: '配貨中' },
        { text: t('核對中'), value: '核對中' },
        { text: t('已包裝'), value: '已包裝' },
        { text: t('待攬收'), value: '待攬收' },
        { text: t('已發貨'), value: '已發貨' },
        { text: t('已收貨'), value: '已收貨' },
        { text: t('申請退貨'), value: '申請退貨' },
        { text: t('退貨處理中'), value: '退貨處理中' },
        { text: t('退貨完成'), value: '退貨完成' },
        { text: t('已取消'), value: '已取消' },
      ],
      onFilter: (value: any, record: OutboundOrder) => record.status === value,
      render: (status: OutboundOrderStatus) => (
          <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
            {statusLabel(status)}
          </Tag>
      ),
    },
    totalValue: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('totalValue')}
          >
            {t('總金額')}
                            {getOutboundSortIcon('totalValue')}
          </div>
      ),
      dataIndex: 'totalValue',
      key: 'totalValue',
      width: 100,
      sorter: true,
      render: (value: number) => (
          <Text strong>¥{(Number(value) || 0).toFixed(2)}</Text>
      ),
    },
    createdAt: {
      title: (
          <div
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleOutboundColumnSort('createdAt')}
          >
            {t('創建時間')}
                            {getOutboundSortIcon('createdAt')}
          </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      sorter: true,
      render: (createdAt: string) => {
          if (!createdAt || typeof createdAt !== 'string') return '-';
          const parsed = dayjs.utc(createdAt).tz('Asia/Hong_Kong');
          if (!parsed.isValid()) return '-';
          return (
              <div>
                <div>{parsed.format('YYYY-MM-DD')}</div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {parsed.format('HH:mm:ss')}
                </Text>
              </div>
          );
      },
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      width: 200,
      fixed: 'right' as 'right',
      render: (_: any, record: OutboundOrder) => (
          <Space size="small" direction="vertical">
            <Space size="small">
              <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewOrder(record)}
                  size="small"
              >
                {t('詳情')}
                                    </Button>
              {!isCustomerRole && (
                <>
                  <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => handleEditOrder(record)}
                      size="small"
                  >
                    {t('編輯')}
                                                </Button>
                  <Popconfirm
                      title={t('確定要刪除此出庫訂單嗎？')}
                      onConfirm={() => handleDeleteOrder(record)}
                      okText={t('確定')}
                      cancelText={t('取消')}
                  >
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                    >
                      {t('刪除')}
                                                      </Button>
                  </Popconfirm>
                </>
              )}
            </Space>
            {!isCustomerRole && (
              <Space direction="vertical" size="small">
                {/* 選擇物流按鈕 - 僅在待發貨、處理中狀態顯示 */}
                {(record.status === '待發貨' || record.status === '處理中' || record.status === '待攬收') && !record.deliveryTrackingNumber && (
                    <Button
                        type="default"
                        size="small"
                        icon={<SendOutlined />}
                        onClick={() => {
                          setDeliveryModalOrder(record);
                          setDeliveryModalVisible(true);
                        }}
                    >
                      {t('選擇物流')}
                                                  </Button>
                )}
                <Button
                    type="primary"
                    size="small"
                    icon={<PrinterOutlined />}
                    onClick={async () => {
                      if(record.waybillNumber) {
                        if(record.orderNumber.includes("H")) {// TODO HKTVmall【根據是否含有H 判斷是否是HKTVmall的 之後可以改（如果有其他規則）】
                          handleHktvMallDownloadPDF(record.orderNumber, record.waybillNumber)
                        }else {
                          handleDownloadPDF(record.orderNumber, record.waybillNumber)
                        }
                      }
                    }}
                >
                  {t('列印')}
                                          </Button>
              {record.status === '待攬收' && (
                  <Button
                      type="link"
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={async () => {
                        let deliveryDate: string = dayjs().format('D/M/YYYY');
                        let deliveryTime: string = '11:00-16:00';
                        let deliveryItemNumber: string = '';
                        let receiverContact: string = '';
                        await new Promise<void>((resolve) => {
                          Modal.confirm({
                            title: t('生成配送單'),
                            content: (
                                <div style={{ marginTop: 8 }}>
                                  <div style={{ marginBottom: 8 }}>
                                    <Text>{t('送貨日期')}</Text>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        defaultValue={dayjs() as any}
                                        onChange={(d) => {
                                          deliveryDate = d ? dayjs(d).format('D/M/YYYY') : '';
                                        }}
                                    />
                                  </div>
                                  <div style={{ marginBottom: 8 }}>
                                    <Text>{t('送貨時間')}</Text>
                                    <Input defaultValue={deliveryTime} onChange={(e) => (deliveryTime = e.target.value)} />
                                  </div>
                                  <div style={{ marginBottom: 8 }}>
                                    <Text>{t('收件人聯絡電話')}</Text>
                                    <Input placeholder={t('可選')} onChange={(e) => (receiverContact = e.target.value)} />
                                  </div>
                                  <div>
                                    <Text>{t('送貨件數')}</Text>
                                    <Input placeholder={t('例如：2 箱')} onChange={(e) => (deliveryItemNumber = e.target.value)} />
                                  </div>
                                </div>
                            ),
                            okText: t('生成並列印'),
                            cancelText: t('取消'),
                            onOk: () => {
                              resolve();
                            },
                          });
                        });

                        try {
                          const dnNumber = `DN-${record.orderNumber}-${dayjs().format('YYYYMMDDHHmmss')}`;
                          const printWindow = window.open('', '_blank');
                          if (!printWindow) {
                            message.error(t('無法打開列印窗口，請檢查瀏覽器彈窗設置'));
                            return;
                          }
                          printWindow.document.write(`<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>正在載入配送單...</title></head><body style=\"font-family: Arial, \\\"Microsoft JhengHei\\\"; padding: 24px; color: #333;\">正在載入配送單模板，請稍候...</body></html>`);
                          printWindow.document.close();

                          const html = await apiService.generateDeliveryNoteHTML({
                            customerName: record.customerName,
                            deliveryDate,
                            deliveryTime,
                            deliveryOrderNumber: dnNumber,
                            receiverName: record.receiver,
                            receiverContact,
                            deliveryAddress: record.deliveryAddress,
                            deliveryItemNumber: deliveryItemNumber || '1 件',
                            orderNumber: record.orderNumber,
                          });
                          printWindow.document.open();
                          printWindow.document.write(html);
                          printWindow.document.close();
                          printWindow.onload = () => {
                            setTimeout(() => {
                              printWindow.print();
                              printWindow.onafterprint = () => {
                                printWindow.close();
                              };
                            }, 300);
                          };
                        } catch (e) {
                          message.error(t('載入配送單模板失敗'));
                          console.error(e);
                        }
                      }}
                  >
                    {t('配送單')}
                                                </Button>
              )}
              <Select
                  size="small"
                  value={record.status}
                  onChange={(value) => handleStatusChange(record.id, value)}
                  style={{ width: 120 }}
                  // Allow status change for orders with stock issues - backend will show confirmation
                  disabled={record.status === '已收貨' || record.status?.includes('退貨') || record.status === '已取消'}
              >
                <Option value="待發貨">{t('待發貨')}</Option>
                <Option value="處理中">{t('處理中')}</Option>
                <Option value="配貨中">{t('配貨中')}</Option>
                <Option value="核對中">{t('核對中')}</Option>
                <Option value="已包裝">{t('已包裝')}</Option>
                <Option value="待攬收">{t('待攬收')}</Option>
                <Option value="已發貨">{t('已發貨')}</Option>
                <Option value="已收貨">{t('已收貨')}</Option>
                <Option value="已取消">{t('已取消')}</Option>
              </Select>
              {record.status === '已收貨' && (
                  <Button
                      type="link"
                      size="small"
                      icon={<WarningOutlined />}
                      onClick={() => handleReturnRequest(record)}
                      style={{ color: '#fa8c16' }}
                  >
                    {t('申請退貨')}
                                                </Button>
              )}
              {record.returnInfo && (
                  <Tag color={record.status?.includes('退貨') ? 'red' : 'orange'}>
                    {record.returnInfo.handlingStatus === 'keep' ? t('保留商品') :
                        record.returnInfo.handlingStatus === 'discard' ? t('丟棄商品') :
                            record.returnInfo.handlingStatus === 'refund' ? t('退款處理') : t('換貨處理')}
                  </Tag>
              )}
              </Space>
            )}
          </Space>
      ),
    }
  };

  // 根據列配置生成顯示的出庫訂單列
  const columns: ColumnsType<OutboundOrder> = Object.entries(outboundColumnSettings)
      .filter(([_, config]: [string, any]) => config.visible)
      .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
      .map(([key]) => {
        const column = allOutboundColumns[key as keyof typeof allOutboundColumns];
        if (!column) {
          console.warn(`Missing outbound column definition for key: ${key}`);
        }
        return column;
      })
      .filter(Boolean); // 過濾掉undefined的列

  // 出庫主表支持拖拽列排序（排除 操作 列）
  const { columns: draggableColumns, order: outboundOrder } = useDraggableColumns<OutboundOrder>({
    columns: columns as any,
    disableDragKeys: ['actions'],
    storageKey: 'outbound-order-table',
  });

  // 無論拖拽/配置如何，強制將 操作 欄固定在最後
  const columnsWithActionsLast = useMemo(() => {
    const arr = (draggableColumns as any[]) || [];
    const idx = arr.findIndex((c: any) => c && (c.key === 'actions' || c.dataIndex === 'actions'));
    if (idx === -1) return arr;
    const copy = arr.slice();
    const [actionsCol] = copy.splice(idx, 1);
    if (actionsCol) {
      const originalRender = actionsCol.render;
      actionsCol.render = (text: any, record: OutboundOrder, index: number) => {
        const disabled = record.hasStockIssues;
        const tooltip = disabled ? t('庫存不足，暫不可操作') : undefined;
        if (typeof originalRender === 'function') {
          const result = originalRender(text, record, index);
          if (React.isValidElement(result)) {
            return React.cloneElement(result as React.ReactElement, { disabled });
          }
        }
        return (
            <Space size="small">
              <Tooltip title={tooltip || t('詳情')}>
                <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewOrder(record)} disabled={disabled} />
              </Tooltip>
              {!isCustomerRole && (
                <>
                  <Tooltip title={tooltip || t('列印')}>
                    <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()} disabled={disabled} />
                  </Tooltip>
                  <Tooltip title={tooltip || t('編輯')}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => setSelectedOrder(record)} disabled={disabled} />
                  </Tooltip>
                  <Tooltip title={tooltip || t('刪除')}>
                    <Popconfirm
                        title={t('確認刪除?')}
                        onConfirm={() => message.info(t('刪除功能暫未實作'))}
                        okText={t('確認')}
                        cancelText={t('取消')}
                        disabled={disabled}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} disabled={disabled} />
                    </Popconfirm>
                  </Tooltip>
                  <Tooltip title={tooltip || t('變更狀態')}>
                    <Select
                        value={record.status}
                        onChange={(value: OutboundOrderStatus) => message.info(t('變更狀態：{{value}}', { value: statusLabel(value) }))}
                        style={{ width: 120 }}
                        disabled={disabled}
                    >
                      {['待發貨','處理中','配貨中','核對中','已包裝','待攬收','已發貨','已收貨','申請退貨','退貨處理中','退貨完成','已取消'].map((s) => (
                          <Option key={s} value={s as any}>{statusLabel(s)}</Option>
                      ))}
                    </Select>
                  </Tooltip>
                </>
              )}
            </Space>
        );
      };
    }
    copy.push(actionsCol);
    return copy;
  }, [draggableColumns]);

  // 拖拽後同步回列配置
  useEffect(() => {
    if (!outboundOrder || outboundOrder.length === 0) return;
    setOutboundColumnSettings((prev: any) => {
      const next: any = { ...prev };
      outboundOrder.forEach((k: string, idx: number) => {
        if (next[k]) next[k] = { ...next[k], order: idx };
      });
      Object.keys(next).forEach((k) => {
        if (!outboundOrder.includes(k)) {
          const o = next[k]?.order ?? 9999;
          next[k] = { ...next[k], order: Math.max(outboundOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('outbound-order-columns-config', JSON.stringify(next));
        localStorage.setItem('outbound-order-columns-version', '2024-01-v4');
      } catch {}
      return next;
    });
  }, [outboundOrder]);

  // 處理出庫訂單列可見性切換
  const handleOutboundColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...outboundColumnSettings,
      [columnKey]: { ...outboundColumnSettings[columnKey], visible }
    };
    setOutboundColumnSettings(newSettings);
    localStorage.setItem('outbound-order-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('outbound-order-columns-version', '2024-01-v4');
  };

  // 顯示所有出庫訂單列
  const showAllOutboundColumns = () => {
    const allVisibleSettings = {
      orderNumber: { visible: true, order: 0 },
      customerName: { visible: true, order: 1 },
      customerCode: { visible: true, order: 2 },
      receiver: { visible: true, order: 3 },
      deliveryAddress: { visible: true, order: 4 },
      courier: { visible: true, order: 5 },
      salesChannel: { visible: true, order: 6 },
      trackingNumber: { visible: true, order: 7 },
      waybill: { visible: true, order: 8 },
      status: { visible: true, order: 9 },
      totalValue: { visible: true, order: 10 },
      createdAt: { visible: true, order: 11 },
      actions: { visible: true, order: 12 }
    };
    setOutboundColumnSettings(allVisibleSettings);
    localStorage.setItem('outbound-order-columns-config', JSON.stringify(allVisibleSettings));
    localStorage.setItem('outbound-order-columns-version', '2024-01-v5');
    message.success(t('所有列已顯示'));
  };

  // 重置出庫訂單列配置
  const resetOutboundColumnSettings = () => {
    const defaultSettings = {
      orderNumber: { visible: true, order: 0 },
      customerName: { visible: true, order: 1 },
      customerCode: { visible: true, order: 2 },
      receiver: { visible: true, order: 3 },
      deliveryAddress: { visible: true, order: 4 },
      courier: { visible: true, order: 5 },
      salesChannel: { visible: true, order: 6 },
      trackingNumber: { visible: false, order: 7 },
      waybill: { visible: true, order: 8 },
      status: { visible: true, order: 9 },
      totalValue: { visible: true, order: 10 },
      createdAt: { visible: true, order: 11 },
      actions: { visible: true, order: 12 }
    };
    setOutboundColumnSettings(defaultSettings);
    localStorage.setItem('outbound-order-columns-config', JSON.stringify(defaultSettings));
    localStorage.setItem('outbound-order-columns-version', '2024-01-v5');
    message.success(t('列配置已重置'));
  };

  // 出庫訂單列設置菜單
  const outboundColumnSettingsMenu = (
    <div style={{
      background: '#fff',
      borderRadius: '2px',
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(allOutboundColumns).map(([key, column]) => (
          <div
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleOutboundColumnVisibilityChange(key, !(outboundColumnSettings[key]?.visible ?? true));
            }}
          >
            <Checkbox
              checked={outboundColumnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleOutboundColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {typeof column.title === 'string' ? column.title :
                  key === 'orderNumber' ? t('訂單號') :
                      key === 'customerName' ? t('客戶名稱') :
                          key === 'customerCode' ? t('客戶代碼') :
                              key === 'receiver' ? t('收件人') :
                                  key === 'deliveryAddress' ? t('配送地址') :
                                      key === 'courier' ? t('快遞商') :
                                          key === 'salesChannel' ? t('銷售渠道') :
                                              key === 'pickUpDate' ? t('取貨日期') :
                                                  key === 'trackingNumber' ? t('追蹤號') :
                                                      key === 'waybill' ? t('運單號') :
                                                          key === 'status' ? t('狀態') :
                                                              key === 'totalValue' ? t('總金額') :
                                                                  key === 'createdAt' ? t('創建時間') :
                                                                      key === 'actions' ? t('操作') : key}
            </Checkbox>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '4px 0' }}>
        <div
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            showAllOutboundColumns();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            {t('顯示所有列')}
                                </Button>
        </div>
        <div
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            resetOutboundColumnSettings();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            {t('重置列設置')}
                                </Button>
        </div>
      </div>
    </div>
  );

  // 處理出庫訂單數據排序
  const sortedAndFilteredOutboundOrders = useMemo(() => {
    let result = [...filteredOrders];

    if (outboundSortField && outboundSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[outboundSortField as keyof OutboundOrder];
        let bValue: any = b[outboundSortField as keyof OutboundOrder];

        // 處理數字字段排序
        if (['totalValue'].includes(outboundSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }

        // 處理日期字段排序
        if (['createdAt', 'pickUpDate'].includes(outboundSortField)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return outboundSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return outboundSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [filteredOrders, outboundSortField, outboundSortOrder]);


  return (
      <div style={{ padding: '24px' }}>
        {/* 最近 24 小時 HKTV 錯誤橫幅（與 HKTVmall Job 頁共用元件） */}
        <HktvRecentErrorsBanner />

        {/* 標題和展開收起按鈕 */}
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <Title level={2} style={{ marginBottom: 0 }}>{t('出庫訂單管理')}</Title>
            <Text type="secondary">{t('管理所有出庫訂單，包括創建新訂單、編輯現有訂單、追蹤發貨狀態等。系統會自動檢查庫存可用性並生成警告。')}</Text>
          </div>
          <Button
              type="text"
              icon={collapsed ? <DownOutlined /> : <UpOutlined />}
              onClick={toggleCollapsed}
              style={{ fontSize: 16 }}
          />
        </div>

        {/* KPI 儀錶板 */}
        {!collapsed && (
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={6}>
                <Card hoverable>
                  <Statistic
                      title={t('總訂單數')}
                      value={statistics.total}
                      prefix={<ShoppingCartOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card hoverable>
                  <Statistic
                      title={t('待發貨')}
                      value={statistics.pending}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card hoverable>
                  <Statistic
                      title={t('處理中')}
                      value={statistics.processing}
                      prefix={<EditOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card hoverable>
                  <Statistic
                      title={t('庫存問題')}
                      value={statistics.stockIssues}
                      prefix={<WarningOutlined />}
                      valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
            </Row>
        )}

        {/* 操作和篩選區域 */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            {/* Hide action buttons for CUSTOMER role (read-only access) */}
            {!isCustomerRole && (
              <Space direction="horizontal" wrap>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddOrder}
                >
                  {t('新增出庫訂單')}
                                              </Button>
                <Button
                    icon={<UploadOutlined />}
                    onClick={() => setBulkUploadVisible(true)}
                >
                  {t('批量上傳')}
                                              </Button>
                <Button
                    icon={<FileExcelOutlined />}
                    onClick={handleExportExcel}
                >
                  {t('導出')}
                                              </Button>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => loadOutboundOrders()}
                    loading={loading}
                >
                  {t('刷新數據')}
                                              </Button>
                <Button
                    icon={<ClockCircleOutlined />}
                    onClick={() => setUpdatePickUpDateModalVisible(true)}
                >
                  {t('Update Pick Up Date')}
                                              </Button>
                <Button
                    icon={<FileTextOutlined />}
                    onClick={() => handleReportClick('waybill')}
                >
                  {t('報告')}
                                              </Button>

                <Button
                    icon={<FileTextOutlined />}
                    onClick={() => handleReportClick('hktvmall')}
                >
                  {t('HKTVmall 訂單摘要')}
                                              </Button>

                <Button
                    icon={<FileTextOutlined />}
                    onClick={() => handleReportClick('bundle-movements')}
                >
                  {t('Bundle Movements')}
                                              </Button>

                {/* 批量狀態變更（操作） */}
                <Dropdown overlay={batchStatusMenu} trigger={["click"]} disabled={!selectedRowKeys || selectedRowKeys.length === 0}>
                  <Button
                    type="primary"
                    danger
                    icon={<EditOutlined />}
                    loading={loading}
                  >
                    {t('批量更改狀態')} {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
                  </Button>
                </Dropdown>
              </Space>
            )}
            {!isCustomerRole && (
              <Space>
                <Input
                    placeholder={t('搜尋訂單號、運單號、收件人、地址...')}
                    prefix={<SearchOutlined />}
                    value={filters.searchText}
                    onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
                    style={{ width: 300 }}
                    allowClear
                />
                {filters.itemSku && (
                  <Tag
                    color="blue"
                    closable
                    onClose={() => setFilters({ ...filters, itemSku: '' })}
                  >
                    {t('SKU:')} {filters.itemSku}
                  </Tag>
                )}
                <Dropdown
                  overlay={outboundColumnSettingsMenu}
                  trigger={['click']}
                  open={outboundColumnSettingsVisible}
                  onOpenChange={setOutboundColumnSettingsVisible}
                >
                  <Button icon={<SettingOutlined />} title={t('列設置')}>
                    {t('列設置')}
                                                    </Button>
                </Dropdown>
              </Space>
            )}
          </div>

          {/* 篩選條件 - Hide for CUSTOMER role */}
          {!isCustomerRole && (
            <>
              <Row gutter={16}>
                <Col xs={24} sm={8} md={6} lg={4}>
                  <Tooltip title={t('按當前處理進度篩選訂單（例如：待發貨/已發貨等）')}>
                    <Select
                        placeholder={t('篩選狀態')}
                        style={{ width: '100%' }}
                        value={filters.status}
                        onChange={(value) => setFilters({ ...filters, status: value })}
                        allowClear
                    >
                      <Option value="待發貨">{t('待發貨')}</Option>
                      <Option value="處理中">{t('處理中')}</Option>
                      <Option value="配貨中">{t('配貨中')}</Option>
                      <Option value="核對中">{t('核對中')}</Option>
                      <Option value="已包裝">{t('已包裝')}</Option>
                      <Option value="待攬收">{t('待攬收')}</Option>
                      <Option value="已發貨">{t('已發貨')}</Option>
                      <Option value="已收貨">{t('已收貨')}</Option>
                      <Option value="申請退貨">{t('申請退貨')}</Option>
                      <Option value="退貨處理中">{t('退貨處理中')}</Option>
                      <Option value="退貨完成">{t('退貨完成')}</Option>
                      <Option value="已取消">{t('已取消')}</Option>
                    </Select>
                  </Tooltip>
                </Col>
                <Col xs={24} sm={8} md={6} lg={4}>
                  <Tooltip title={t('按是否有庫存問題進行篩選（有庫存問題/庫存正常）')}>
                    <Select
                        placeholder={t('篩選庫存狀態')}
                        style={{ width: '100%' }}
                        value={filters.hasStockIssues}
                        onChange={(value) => setFilters({ ...filters, hasStockIssues: value })}
                        allowClear
                    >
                      <Option value="true">{t('有庫存問題')}</Option>
                      <Option value="false">{t('庫存正常')}</Option>
                    </Select>
                  </Tooltip>
                </Col>
                <Col xs={24} sm={8} md={6} lg={4}>
                  <Tooltip title={t('按客戶（商戶）篩選訂單')}>
                    <Select
                        placeholder={t('篩選客戶')}
                        style={{ width: '100%' }}
                        value={filters.customer || undefined}
                        onChange={(value) => setFilters({ ...filters, customer: value || '' })}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                      {customers.map((c) => (
                        <Option key={c.value} value={c.value}>{c.label}</Option>
                      ))}
                    </Select>
                  </Tooltip>
                </Col>
                <Col xs={24} sm={8} md={6} lg={4}>
                  <Tooltip title={t('按來源平台或渠道篩選（例如：HKTV Mall、Shopify、Amazon）')}>
                    <Select
                        placeholder={t('篩選銷售渠道')}
                        style={{ width: '100%' }}
                        value={filters.salesChannel}
                        onChange={(value) => setFilters({ ...filters, salesChannel: value })}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                      <Option value="HKTVMALL">{t('HKTVmall')}</Option>
                      <Option value="SHOPIFY">{t('Shopify')}</Option>
                      <Option value="MANUAL">{t('手動建立')}</Option>
                    </Select>
                  </Tooltip>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Tooltip title={t('按取貨日期範圍篩選訂單')}>
                    <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        placeholder={['取貨開始日期', '取貨結束日期']}
                        value={filters.pickUpDateRange ? [dayjs(filters.pickUpDateRange[0]), dayjs(filters.pickUpDateRange[1])] : undefined}
                        onChange={(dates) => {
                          if (dates && dates[0] && dates[1]) {
                            setFilters({
                              ...filters,
                              pickUpDateRange: [
                                `${dates[0].startOf('day').format('YYYY-MM-DD')} 00:00:00`,
                                `${dates[1].endOf('day').format('YYYY-MM-DD')} 23:59:59.999`
                              ]
                            });
                          } else {
                            setFilters({ ...filters, pickUpDateRange: undefined });
                          }
                        }}
                        allowClear
                    />
                  </Tooltip>
                </Col>
              </Row>

              {/* 第二行篩選條件 */}
              <Row gutter={16} style={{ marginTop: 16 }}>

                <Col xs={24} sm={12} md={8} lg={6}>
                  <Tooltip title={t('按店鋪ID篩選訂單（主要用於HKTV Mall訂單）- 可多選')}>
                    <Select
                        mode="multiple"
                        placeholder={t('店鋪ID（可多選）')}
                        style={{ width: '100%' }}
                        value={filters.storeId}
                        onChange={(value) => setFilters({ ...filters, storeId: value || [] })}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={hktvAccounts.map(account => ({
                          value: account.storeCode,
                          label: account.storeCode
                        }))}
                        maxTagCount="responsive"
                    />
                  </Tooltip>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Tooltip title={t('按 Run Tag 篩選訂單（根據 tag 關聯的 store codes 和 warehouse IDs 過濾）- 可多選')}>
                    <Select
                        mode="multiple"
                        placeholder={t('篩選 Run Tag（可多選）')}
                        style={{ width: '100%' }}
                        value={filters.runTagId}
                        onChange={(value) => setFilters({ ...filters, runTagId: value || [] })}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={runTags.map(tag => ({
                          value: tag.id,
                          label: `${tag.tagName} (${tag.storeCodes.length} stores)`
                        }))}
                        maxTagCount="responsive"
                    />
                  </Tooltip>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Tooltip title={t('按創建日期範圍篩選訂單')}>
                    <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        placeholder={['創建開始日期', '創建結束日期']}
                        value={filters.createdDateRange ? [dayjs(filters.createdDateRange[0]), dayjs(filters.createdDateRange[1])] : undefined}
                        onChange={(dates) => {
                          if (dates && dates[0] && dates[1]) {
                            setFilters({
                              ...filters,
                              createdDateRange: [
                                `${dates[0].startOf('day').format('YYYY-MM-DD')} 00:00:00`,
                                `${dates[1].endOf('day').format('YYYY-MM-DD')} 23:59:59.999`
                              ]
                            });
                          } else {
                            setFilters({ ...filters, createdDateRange: undefined });
                          }
                        }}
                        allowClear
                        format="YYYY-MM-DD"
                    />
                  </Tooltip>
                </Col>
              </Row>
            </>
          )}
        </Card>

        {/* 出庫訂單表格 */}
        <Card>
          <Table
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                // Allow selecting orders with stock issues - user can confirm oversell
                getCheckboxProps: () => ({ disabled: false })
              }}
              columns={columnsWithActionsLast as any}
              dataSource={sortedAndFilteredOutboundOrders}
              rowKey="id"
              loading={loading}
              pagination={{
                total: total,
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 200],
                showTotal: (total, range) =>
                    t('顯示 {{from}}-{{to}} 項，共 {{total}} 項記錄', { from: range[0], to: range[1], total }),
                onChange: (p, ps) => {
                  if (ps && ps !== pageSize) {
                    setPageSize(ps);
                    setCurrentPage(1);
                  } else {
                    setCurrentPage(p);
                  }
                }
              }}
              scroll={{ x: 1600 }}
              rowClassName={(record) => record.hasStockIssues ? 'row-stock-issue' : ''}
          />
        </Card>

        {/* 訂單詳情抽屜 */}
        <Drawer
            title={t('出庫訂單詳情')}
            placement="right"
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            width={800}
        >
          {selectedOrder && (
              <div>
                {/* 基本信息 */}
                <Card title={t('訂單信息')} style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <p><strong>{t('訂單號：')}</strong>{selectedOrder.orderNumber}</p>
                      <p><strong>{t('客戶：')}</strong>{selectedOrder.customerName} ({selectedOrder.customerCode})</p>
                      <p><strong>{t('收件人：')}</strong>{selectedOrder.receiver}</p>
                      <p><strong>{t('配送地址：')}</strong>{selectedOrder.deliveryAddress}</p>
                    </Col>
                    <Col span={12}>
                      <p><strong>{t('快遞商：')}</strong>{selectedOrder.courier}</p>
                      <p><strong>{t('追蹤號：')}</strong>{selectedOrder.fedexTrackingNo || selectedOrder.waybillNumber || selectedOrder.deliveryTrackingNumber || '-'}</p>
                      <p><strong>{t('狀態：')}</strong>
                        <Tag color={getStatusColor(selectedOrder.status)} style={{ marginLeft: 8 }}>
                          {statusLabel(selectedOrder.status)}
                        </Tag>
                      </p>
                      <p><strong>{t('總金額：')}</strong>¥{(Number(selectedOrder.totalValue) || 0).toFixed(2)}</p>
                      {(Number(selectedOrder.orderDiscount) || 0) > 0 && (
                        <p><strong>{t('訂單折扣：')}</strong><span style={{ color: '#f5222d' }}>-¥{(Number(selectedOrder.orderDiscount) || 0).toFixed(2)}</span></p>
                      )}
                    </Col>
                  </Row>
                </Card>

                {/* FedEx 運單標籤 - 當有 FedEx 標籤時顯示 */}
                {selectedOrder.fedexLabelPdf && (
                    <Card
                      title={
                        <Space>
                          <span>{t('FedEx 運單標籤')}</span>
                          <Tag color="purple">{t('FedEx')}</Tag>
                        </Space>
                      }
                      style={{ marginBottom: 16 }}
                      extra={
                        <Space>
                          <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => {
                              try {
                                const byteCharacters = atob(selectedOrder.fedexLabelPdf!);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: 'application/pdf' });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `fedex-label-${selectedOrder.orderNumber}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                message.success(t('FedEx 運單標籤已下載'));
                              } catch (e) {
                                message.error(t('下載失敗'));
                              }
                            }}
                          >
                            {t('下載標籤')}
                                                            </Button>
                          <Button
                            icon={<PrinterOutlined />}
                            onClick={() => {
                              try {
                                const byteCharacters = atob(selectedOrder.fedexLabelPdf!);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: 'application/pdf' });
                                const url = window.URL.createObjectURL(blob);
                                const printWindow = window.open(url);
                                if (printWindow) {
                                  printWindow.onload = () => {
                                    printWindow.print();
                                  };
                                }
                              } catch (e) {
                                message.error(t('列印失敗'));
                              }
                            }}
                          >
                            {t('列印標籤')}
                                                            </Button>
                        </Space>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <p><strong>{t('快遞商：')}</strong>{t('FedEx')}</p>
                          <p><strong>{t('追蹤號：')}</strong>{selectedOrder.fedexTrackingNo || selectedOrder.deliveryTrackingNumber || '-'}</p>
                        </Col>
                        <Col span={12}>
                          <p>
                            <strong>{t('追蹤連結：')}</strong>
                            {(selectedOrder.fedexTrackingNo || selectedOrder.deliveryTrackingNumber) ? (
                              <a
                                href={`https://www.fedex.com/fedextrack/?trknbr=${selectedOrder.fedexTrackingNo || selectedOrder.deliveryTrackingNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ marginLeft: 8 }}
                              >
                                {t('在 FedEx 官網追蹤')}
                                                                                </a>
                            ) : '-'}
                          </p>
                        </Col>
                      </Row>
                    </Card>
                )}

                {/* FedEx 商業發票下載 - 當有 FedEx 運單時顯示（ETD 電子報關） */}
                {selectedOrder.fedexTrackingNo && selectedOrder.deliveryProvider === 'FedEx' && (
                    <Card
                      title={
                        <Space>
                          <span>{t('商業發票 (Commercial Invoice)')}</span>
                          <Tag color="blue">ETD</Tag>
                        </Space>
                      }
                      style={{ marginBottom: 16 }}
                      size="small"
                    >
                      <Row gutter={16} align="middle">
                        <Col span={16}>
                          <p style={{ marginBottom: 0 }}>
                            <strong>{t('說明：')}</strong>{t('FedEx ETD 電子報關自動生成的商業發票，用於國際海關清關。')}
                                                                    </p>
                        </Col>
                        <Col span={8} style={{ textAlign: 'right' }}>
                          <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={async () => {
                              try {
                                message.loading({ content: t('正在下載商業發票...'), key: 'downloadInvoice' });
                                const blob = await apiService.getFedExCommercialInvoice(selectedOrder.id);
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `commercial-invoice-${selectedOrder.orderNumber}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                message.success({ content: t('商業發票已下載'), key: 'downloadInvoice' });
                              } catch (e: any) {
                                console.error('Failed to download commercial invoice:', e);
                                message.error({ content: e.message || t('下載商業發票失敗'), key: 'downloadInvoice' });
                              }
                            }}
                          >
                            {t('下載商業發票')}
                                                                    </Button>
                        </Col>
                      </Row>
                    </Card>
                )}

                {/* SF Express 運單標籤 - 當 delivery_provider 為 SF Express 或 SF_B2B 且有追蹤號時顯示 */}
                {(selectedOrder.deliveryProvider === 'SF Express' || selectedOrder.deliveryProvider === 'SF_B2B') && (selectedOrder.deliveryTrackingNumber || selectedOrder.waybillNumber) && (
                    <Card
                      title={
                        <Space>
                          <span>{t('順豐速運運單標籤')}</span>
                          <Tag color="red">{selectedOrder.deliveryProvider === 'SF_B2B' ? t('SF Express B2B') : t('SF Express')}</Tag>
                        </Space>
                      }
                      style={{ marginBottom: 16 }}
                      extra={
                        <Space>
                          {selectedOrder.sfExpressLabelPdf && (
                            <>
                              <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={() => {
                                  try {
                                    const byteCharacters = atob(selectedOrder.sfExpressLabelPdf!);
                                    const byteNumbers = new Array(byteCharacters.length);
                                    for (let i = 0; i < byteCharacters.length; i++) {
                                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                                    }
                                    const byteArray = new Uint8Array(byteNumbers);
                                    const blob = new Blob([byteArray], { type: 'application/pdf' });
                                    const url = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = `sf-express-label-${selectedOrder.orderNumber}.pdf`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                    message.success(t('順豐運單標籤已下載'));
                                  } catch (e) {
                                    message.error(t('下載失敗'));
                                  }
                                }}
                              >
                                {t('下載標籤')}
                                                                        </Button>
                              <Button
                                icon={<PrinterOutlined />}
                                onClick={() => {
                                  try {
                                    const byteCharacters = atob(selectedOrder.sfExpressLabelPdf!);
                                    const byteNumbers = new Array(byteCharacters.length);
                                    for (let i = 0; i < byteCharacters.length; i++) {
                                      byteNumbers[i] = byteCharacters.charCodeAt(i);
                                    }
                                    const byteArray = new Uint8Array(byteNumbers);
                                    const blob = new Blob([byteArray], { type: 'application/pdf' });
                                    const url = window.URL.createObjectURL(blob);
                                    const printWindow = window.open(url);
                                    if (printWindow) {
                                      printWindow.onload = () => {
                                        printWindow.print();
                                      };
                                    }
                                  } catch (e) {
                                    message.error(t('列印失敗'));
                                  }
                                }}
                              >
                                {t('列印標籤')}
                                                                        </Button>
                            </>
                          )}
                        </Space>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <p><strong>{t('快遞商：')}</strong>{t('順豐速運 ({{service}})', { service: selectedOrder.deliveryProvider === 'SF_B2B' ? t('SF Express B2B') : t('SF Express') })}</p>
                          <p><strong>{t('追蹤號：')}</strong>{selectedOrder.deliveryTrackingNumber || selectedOrder.waybillNumber || '-'}</p>
                        </Col>
                        <Col span={12}>
                          <p>
                            <strong>{t('追蹤連結：')}</strong>
                            {(selectedOrder.deliveryTrackingNumber || selectedOrder.waybillNumber) ? (
                              <a
                                href={`https://www.sf-express.com/hk/tc/support/querySupport/waybill/?No=${selectedOrder.deliveryTrackingNumber || selectedOrder.waybillNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ marginLeft: 8 }}
                              >
                                {t('在順豐官網追蹤')}
                                                                                </a>
                            ) : '-'}
                          </p>
                          {!selectedOrder.sfExpressLabelPdf && selectedOrder.deliveryTrackingNumber && (
                            <div style={{ marginTop: 8 }}>
                              <p style={{ color: '#ff4d4f', marginBottom: 8 }}>
                                <strong>{t('提示：')}</strong>{t('運單標籤尚未生成')}
                                                                                </p>
                              <Button
                                type="primary"
                                size="small"
                                icon={<ReloadOutlined />}
                                loading={retryingLabel}
                                onClick={async () => {
                                  if (!selectedOrder.id) return;
                                  setRetryingLabel(true);
                                  try {
                                    const accountType = selectedOrder.deliveryProvider === 'SF_B2B' ? 'b2b' : 'standard';
                                    const response: any = await apiService.retrySFExpressOutboundLabel(selectedOrder.id, accountType);
                                    if (response?.success) {
                                      message.success(t('運單標籤下載成功！正在重新載入訂單資料...'));
                                      // Reload the order to get the new PDF
                                      handleViewOrder(selectedOrder);
                                    } else {
                                      message.error(response?.error || response?.message || t('下載運單標籤失敗'));
                                    }
                                  } catch (error: any) {
                                    console.error('Failed to retry label download:', error);
                                    // Try to extract error details from the response
                                    const errorMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message || t('下載運單標籤失敗');
                                    const hint = error?.response?.data?.hint;
                                    if (hint) {
                                      message.error(t('{{errorMsg}}\n提示：{{hint}}', { errorMsg, hint }), 8);
                                    } else {
                                      message.error(errorMsg, 5);
                                    }
                                  } finally {
                                    setRetryingLabel(false);
                                  }
                                }}
                              >
                                {t('重新下載標籤')}
                                                                                </Button>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Card>
                )}

                {/* 退貨信息 */}
                {selectedOrder.returnInfo && (
                    <Card title={t('退貨信息')} style={{ marginBottom: 16 }}>
                      <Row gutter={16}>
                        <Col span={12}>
                          <p><strong>{t('退貨原因：')}</strong>{selectedOrder.returnInfo.reason}</p>
                          <p><strong>{t('申請時間：')}</strong>{dayjs(selectedOrder.returnInfo.requestDate).format('YYYY-MM-DD HH:mm:ss')}</p>
                          <p><strong>{t('處理方式：')}</strong>
                            <Tag color={
                              selectedOrder.returnInfo.handlingStatus === 'keep' ? 'green' :
                                  selectedOrder.returnInfo.handlingStatus === 'discard' ? 'red' :
                                      selectedOrder.returnInfo.handlingStatus === 'refund' ? 'orange' : 'blue'
                            }>
                              {selectedOrder.returnInfo.handlingStatus === 'keep' ? t('保留商品') :
                                  selectedOrder.returnInfo.handlingStatus === 'discard' ? t('丟棄商品') :
                                      selectedOrder.returnInfo.handlingStatus === 'refund' ? t('退款處理') : t('換貨處理')}
                            </Tag>
                          </p>
                        </Col>
                        <Col span={12}>
                          <p><strong>{t('商家確認：')}</strong>{selectedOrder.returnInfo.merchantConfirmation || t('待確認')}</p>
                          <p><strong>{t('處理人員：')}</strong>{selectedOrder.returnInfo.confirmedBy || '-'}</p>
                          <p><strong>{t('庫存調整：')}</strong>
                            {selectedOrder.returnInfo.inventoryAdjusted ?
                                <Tag color="green">{t('已調整')}</Tag> :
                                <Tag color="orange">{t('未調整')}</Tag>}
                          </p>
                        </Col>
                      </Row>
                      {selectedOrder.returnInfo.handlingRemark && (
                          <div>
                            <Divider />
                            <p><strong>{t('處理備註：')}</strong></p>
                            <Text>{selectedOrder.returnInfo.handlingRemark}</Text>
                          </div>
                      )}
                    </Card>
                )}

                {/* 產品清單 */}
                <Card title={t('產品清單')}>
                  <Table
                      dataSource={selectedOrder.items}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      columns={[
                        {
                          title: t('產品'),
                          key: 'product',
                          render: (_, item) => (
                              <div>
                                <div style={{ fontWeight: 500 }}>{item.productNameEN}</div>
                                <div style={{ fontSize: '12px', color: '#666' }}>{item.productNameCN}</div>
                                <div style={{ fontSize: '12px', color: '#999' }}>{t('SKU:')} {item.skuId}</div>
                              </div>
                          ),
                        },
                        {
                          title: t('數量'),
                          key: 'quantity',
                          width: 120,
                          render: (_, item) => (
                              <div>
                                <div>{t('包裝:')} {item.quantityPackage}</div>
                                <div>{t('基本單位:')} {item.quantityBaseUnit}</div>
                              </div>
                          ),
                        },
                        {
                          title: t('庫存狀態'),
                          key: 'stock',
                          width: 120,
                          render: (_, item) => (
                              <div>
                                <div>{t('可用:')} {item.availableStock}</div>
                                {item.isStockInsufficient && (
                                    <Tag color="red">{t('庫存不足')}</Tag>
                                )}
                              </div>
                          ),
                        },
                        {
                          title: t('單價'),
                          dataIndex: 'unitPrice',
                          width: 100,
                          render: (price) => `¥${(Number(price) || 0).toFixed(2)}`,
                        },
                        {
                          title: t('折扣'),
                          dataIndex: 'discountAmount',
                          width: 100,
                          render: (discount) => {
                            const amount = Number(discount) || 0;
                            return amount > 0 ? <span style={{ color: '#f5222d' }}>-¥{amount.toFixed(2)}</span> : '-';
                          },
                        },
                      ]}
                  />
                </Card>
              </div>
          )}
        </Drawer>

        {/* 新增/編輯表單 */}
        <OutboundOrderForm
            visible={formVisible}
            order={editingOrder}
            customers={customers}
            products={mockProducts}
            couriers={courierOptions}
            defaultCouriers={DEFAULT_COURIERS}
            onAddCourier={handleAddCourier}
            onRemoveCourier={handleRemoveCourier}
            countryCodes={mockCountryCodes(t)}
            allocateRules={allocateRules(t)}
            warehouses={warehouses}
            onSubmit={handleFormSubmit}
            onCancel={() => setFormVisible(false)}
        />

        {/* 批量上傳彈窗 */}
        <OutboundBulkUploadModal
            visible={bulkUploadVisible}
            onCancel={() => setBulkUploadVisible(false)}
            onUploadComplete={handleBulkUploadComplete}
        />

        {/* 退貨處理彈窗 */}
        <ReturnOrderModal
            visible={returnModalVisible}
            order={returningOrder}
            onCancel={() => {
              setReturnModalVisible(false);
              setReturningOrder(null);
            }}
            onSubmit={handleReturnSubmit}
        />

        {/* 選擇物流服務商彈窗 */}
        <DeliveryProviderModal
            visible={deliveryModalVisible}
            onClose={() => {
              setDeliveryModalVisible(false);
              setDeliveryModalOrder(null);
            }}
            outboundOrderId={deliveryModalOrder?.id || ''}
            outboundNumber={deliveryModalOrder?.orderNumber || ''}
            recipientName={deliveryModalOrder?.receiver}
            recipientAddress={deliveryModalOrder?.deliveryAddress}
            recipientCountryCode={deliveryModalOrder?.countryCode}
            recipientPhone={deliveryModalOrder?.receiverPhone}
            recipientPostalCode={deliveryModalOrder?.postalCode}
            recipientCity={deliveryModalOrder?.city}
            recipientState={deliveryModalOrder?.province}
            totalAmount={deliveryModalOrder?.totalValue}
            onSuccess={(trackingNumber) => {
              message.success(t('物流單號已生成：{{trackingNumber}}', { trackingNumber }));
              setDeliveryModalVisible(false);
              setDeliveryModalOrder(null);
              loadOutboundOrders();
            }}
        />

        {/* Update Pick Up Date Modal */}
        <Modal
            title={t('Update Pick Up Date')}
            open={updatePickUpDateModalVisible}
            onOk={handleUpdatePickUpDate}
            onCancel={() => {
              setUpdatePickUpDateModalVisible(false);
              setUpdatePickUpDateRange(null);
            }}
            confirmLoading={updatePickUpDateLoading}
            okText={t('更新')}
            cancelText={t('取消')}
        >
          <div style={{ marginBottom: 16 }}>
            <Text>{t('請選擇取貨日期範圍（最多7天）：')}</Text>
          </div>
          <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={['取貨開始日期', '取貨結束日期']}
              value={updatePickUpDateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  const daysDiff = dates[1].diff(dates[0], 'days');
                  if (daysDiff > 7) {
                    message.warning(t('日期範圍不能超過7天'));
                    return;
                  }
                  setUpdatePickUpDateRange([dates[0], dates[1]]);
                } else {
                  setUpdatePickUpDateRange(null);
                }
              }}
              disabledDate={(current) => {
                if (!updatePickUpDateRange || !updatePickUpDateRange[0]) {
                  return false;
                }
                const startDate = updatePickUpDateRange[0];
                const maxDate = startDate.add(7, 'days');
                const minDate = startDate.subtract(7, 'days');
                return current && (current > maxDate || current < minDate);
              }}
          />
        </Modal>

        {/* 報告確認彈窗 */}
        <Modal
            title={reportType === 'waybill' ? t('生成報告') : reportType === 'hktvmall' ? t('生成 HKTVmall 訂單摘要報告') : t('生成 Bundle Movements 報告')}
            open={reportModalVisible}
            onOk={handleReportConfirm}
            onCancel={handleReportCancel}
            okText={t('確認生成')}
            cancelText={t('取消')}
        >
          {reportType === 'bundle-movements' ? (
            <>
              <p><strong>{t('報告名稱：')}</strong>{t('Bundle Movements Report')}</p>
              <Form form={reportForm}>
                <Form.Item label={t('Pickup Date Range')} name="bundlePickupDateRange" rules={[{ required: true, message: t('請選擇取貨日期範圍') }]}>
                  <DatePicker.RangePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label={t('Store Codes')} name="bundleStoreCodes" rules={[{ required: true, message: t('請選擇店鋪') }]}>
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder={t('請選擇店鋪 (e.g. H6620001)')}
                    options={hktvAccounts.map(account => ({
                      label: account.storeCode,
                      value: account.storeCode
                    }))}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Form>
            </>
          ) : reportType === 'waybill' ? (
            <>
              <p><strong>{t('報告名稱：')}</strong>pickup date waybill list</p>
              <Form form={reportForm}>
                <Form.Item label={t('取貨日期範圍')} name="pickUpDateRange" rules={[{ required: true, message: t('請選擇取貨日期範圍') }]}>
                  <DatePicker.RangePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label={t('揀貨創建時間')} name="pickOrderCreateDateRange">
                  <DatePicker.RangePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label={t('標籤選擇')} name="tagSelection">
                  <Select
                    mode="multiple"
                    placeholder={t('請選擇標籤')}
                    options={runTags.map(tag => ({
                      label: tag.tagName,
                      value: tag.id
                    }))}
                    optionFilterProp="children"
                  />
                </Form.Item>
                <Form.Item name="excludeTagSelection" valuePropName="checked">
                  <Checkbox>{t('排除所選標籤')}</Checkbox>
                </Form.Item>
              </Form>
            </>
          ) : (
            <>
              <p><strong>{t('報告名稱：')}</strong>hktvmall order summary</p>
              <Form form={reportForm}>
                <Form.Item label={t('Creation Date')} name="dateRange">
                  <DatePicker.RangePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label={t('Pickup Date')} name="pickupDateRange">
                  <DatePicker.RangePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label={t('Delivery Date')} name="deliveryDateRange">
                  <DatePicker.RangePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label={t('店鋪 ID')} name="storeId">
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder={t('請選擇店鋪 ID')}
                    options={hktvAccounts.map(account => ({
                      label: account.storeCode,
                      value: account.storeCode
                    }))}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
                <Form.Item label={t('標籤選擇')} name="tagSelection">
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder={t('請選擇標籤')}
                    options={runTags.map(tag => ({
                      label: tag.tagName,
                      value: tag.id
                    }))}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
                <Form.Item name="excludeTagSelection" valuePropName="checked">
                  <Checkbox>{t('排除所選標籤')}</Checkbox>
                </Form.Item>
              </Form>
            </>
          )}
        </Modal>

        <style>{`
        .row-stock-issue {
          background-color: #fff2f0 !important;
        }
        .row-stock-issue:hover {
          background-color: #ffa39e !important;
        }
      `}</style>
      </div>
  );
};

export default OutboundOrderPage;